/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Core recommendation engine - pure computation logic
 * No DOM access, no side effects, deterministic output
 */

import type { SystemRecInput, SystemRecOutput, RecommendationOption, SystemType, RetrofitLevel, LowTempReadiness } from './types';
import { RULESET_VERSION } from './rulesetVersion';
import { analyzeContamination, type ContaminationAnalysis } from './hydronics/contamination';
import {
  calculateBaselineCost,
  compareToBaseline,
  contaminationSeverityToSludgeRisk,
  estimateAnnualHeatDemand,
  type BaselineResult,
  type BaselineInput,
  type SludgeRisk
} from './baseline';
import { calculateLongevity } from './longevity';
import { generateMoneyImpact } from './moneyImpact';
import { generateYear1Comparison } from './year1Impact';
import { resolveSedbuk } from './resolveSedbuk';
import { getScorePenalty } from './heatPumpConfidence';
import {
  type CyclingRisk,
  determineCyclingRisk,
  calculateLoadShapingBenefit,
  CYCLING_LOSS_PENALTY_BY_RISK,
  LOAD_SHAPING_LIMITS,
  getEfficiencyFromBand,
  resolveSeasonalEfficiency,
  type SedbukBand,
} from './efficiency';
import type { SeasonalEfficiency } from './types';
import { matchCapex, collectUnusedOverrides } from './pricingOverrides';
import { buildDecisionSummary } from './summary';
import { generateVisualModel } from './visualModel';
import { resolveBaselineSmartControl } from './smartControlResolver';
import { normalizeBoilerAge } from './brandDesignLife';
import { deriveRetrofitMetrics } from './deriveRetrofitMetrics';
import { generateHomeSummaryFacts } from './homeSummaryFacts';
import { evaluateExperienceFit } from './experienceFit';
import { computeConfidenceSummary } from './confidenceAssessment';
import { generateRedFlags } from './redFlagsGenerator';
import { generateWhatCouldChange } from './whatChanges';
import { buildManifestFor } from './assumptions';
import { computeOptionFitScore } from './fitScoring';
import { selectColdMains, getColdMainsWithDefaults } from './coldMainsSelector';
import { getScopClaim, isLowTempConfirmed, getScopAsIs, getScopWithUpgrades } from './performanceClaims';
import { computeOptionCosting } from './costing/costModel';
import {
  type SystemBlueprint,
  type EvaluationContext,
  blueprintFromLegacyConfig,
  buildEvaluationContext,
} from './systemBlueprint';
import {
  calculateWholeHouseHeatLoss,
  buildHeatLossSummary,
  calculateSimpleWholeHouseHeatLoss,
} from '../domain/heatLoss/calculateHeatLoss';
import type { SimpleHeatLossResult } from '../domain/heatLoss/types';

// ==================== CONSTANTS ====================

/** Boiler types that use gas as fuel */
const GAS_BOILER_TYPES = ['combi', 'system', 'regular', 'storage_combi'] as const;

/** Hot water draws estimation constants */
const DRAWS_PER_PERSON = 2;          // Morning and evening showers/baths
const DRAWS_PER_BATHROOM = 1.5;      // Additional capacity per bathroom
const BASE_KITCHEN_DRAWS = 4;        // Kitchen, washing, misc daily draws

/** Conversion factors for estimating missing data */
const BATHROOMS_TO_OCCUPANTS_FACTOR = 1.5;  // Estimate occupants from bathrooms (1.5x)
const OCCUPANTS_TO_BATHROOMS_FACTOR = 0.5;  // Estimate bathrooms from occupants (half, rounded up)

// ==================== HELPER FUNCTIONS ====================

/**
 * Check if a boiler type uses gas as fuel
 */
function isGasBoiler(boilerType: string): boolean {
  return (GAS_BOILER_TYPES as readonly string[]).includes(boilerType);
}

/**
 * Calculate estimated draws per day from occupancy and bathrooms
 * Returns an informed estimate when data is available, or a default when not
 */
function calculateEstimatedDrawsPerDay(
  occupancy: number | undefined,
  bathrooms: number | undefined
): number {
  if (occupancy !== undefined || bathrooms !== undefined) {
    // If we have occupancy or bathrooms, we can make an informed estimate (not an assumption!)
    const effectiveOccupants = occupancy ?? 
      (bathrooms ? bathrooms * BATHROOMS_TO_OCCUPANTS_FACTOR : 3);
    const effectiveBathrooms = bathrooms ?? 
      (occupancy ? Math.ceil(occupancy * OCCUPANTS_TO_BATHROOMS_FACTOR) : 2);
    // Base formula: DRAWS_PER_PERSON per person + DRAWS_PER_BATHROOM per bathroom + BASE_KITCHEN_DRAWS
    return Math.round(effectiveOccupants * DRAWS_PER_PERSON + effectiveBathrooms * DRAWS_PER_BATHROOM + BASE_KITCHEN_DRAWS);
  }
  return 22; // Only use default if we have no data at all
}

// LCOO (Lifecycle Cost of Ownership) types
type LcooInput = {
  periodYears: number;                     // e.g. 20
  annualUsefulHeatKwh?: number;            // optional; if missing we estimate
  tariffs: {
    gasPPerKwh: number;                    // 5.93
    elecPPerKwh: number;                   // 27.69
  };
  perOption: Record<string, {
    upfrontGrossGBP: number;               // user input
    upfrontIsBeforeGrants: boolean;        // user toggle
    applyBUSGrant: boolean;                // user toggle (heat pumps only)
    busGrantAmountGBP?: number;            // default 7500
    annualMaintenanceGBP?: number;         // optional override
    replacementCostFactor?: number;        // default 1.0 (same as upfrontNet)
    retainExistingCylinder?: boolean;      // retain existing cylinder instead of replacing
    existingCylinderAgeYears?: number;     // age of existing cylinder (if retained)
    cylinderCostGBP?: number;              // cost of cylinder component (if known separately)
  }>;
  valueWeighting?: {
    enabled: boolean;
    strengthPct: number;   // 0–25
  };
};

type LcooBreakdown = {
  periodYears: number;
  upfrontGrossGBP: number;
  grantsGBP: number;
  upfrontNetGBP: number;

  annualUsefulHeatKwh: number;
  costPerKwhGBP: number;
  annualRunningGBP: number;

  annualMaintenanceGBP: number;

  systemLifespanYears: number;
  replacementCount: number;
  replacementCostTotalGBP: number;

  totalCostGBP: number;
  annualizedCostGBP: number;

  assumptions: string[];
  warnings: string[];
};

// System performance data
const systemData = {
  combi: {
    baseWaste: {
      "flat/bungalow": 1,
      "terraced": 5.5,
      "semi": 5.5,
      "detached": 8
    },
    baseWait: {
      "flat/bungalow": 20,
      "terraced": 45,
      "semi": 45,
      "detached": 90
    },
    startupKWh: 0.025,
    postPurgeKWh: 0.015
  },
  storageCombi: {
    // Storage combi (e.g., Viessmann Vitodens 111-W with 46L tank)
    baseWaste: {
      "flat/bungalow": 0.8,
      "terraced": 3.0,
      "semi": 3.0,
      "detached": 4.5
    },
    baseWait: {
      "flat/bungalow": 15,
      "terraced": 25,
      "semi": 25,
      "detached": 35
    },
    startupKWh: 0.02,
    postPurgeKWh: 0.01,
    flowRate: 18,  // L/min sustained from 46L integrated tank
    // Storage combi penalty factors (based on real-world data)
    // Penalties reflect: size disadvantage vs wall-hung combis, 10-15yr lifespan with more
    // failure points, expensive specialist repairs, and 15-25% higher total cost over 10yrs
    penalties: {
      sizePenalty: -15,           // Not compact, washing-machine sized (720-900mm × 440-600mm × 480-600mm)
      reliabilityPenalty: -20,    // More complex = more failure points (dual system: combi + storage)
      repairCostPenalty: -15,     // Expensive parts, less commonly stocked
      lifespanPenalty: -10,       // Shorter economical life vs alternatives
      totalOwnershipPenalty: -20  // Higher lifetime cost (purchase £1,500-2,500, repairs, maintenance)
    }
  },
  stored: {
    wastePerDraw: 1.5,  // litres - Source: SAP 10.2 dead-leg assumptions
    wait: 10,  // seconds - typical for well-designed systems
    cylLoss: 1.5  // kWh/day - Source: SAP 10.2 typical unvented cylinder standing loss
  },
  heatPump: {
    // Air Source Heat Pump data (UK 2026)
    // Sources: BEAMA Heat Pump Performance Data, MCS Installation Standards
    ashp: {
      scop: 3.65,  // Midpoint of realistic range (2.8-4.5) - used as baseline reference for simple comparisons
      scopRange: { min: 2.8, max: 4.5 },  // Full range from standard rads to low-temp optimized - Source: BEAMA 2024 + MCS data
      flowTemp: 45,  // °C, typical for retrofits - Source: Heat Pump Association guidance
      optimalFlowTemp: 35,  // °C, for underfloor or oversized radiators
      capExBeforeGrant: 11000,  // £ average - Source: MCS installer survey 2024
      busGrant: 7500,  // £ Boiler Upgrade Scheme 2026 - Source: DESNZ
      netCapEx: 3500,  // £ after grant
      radiatorUpgradeCost: 4500,  // £ average for K3/Type 22 radiators
      pipeworkUpgradeCost: 2000,  // £ average for 28mm mains
      lifespan: 22,  // years - Source: Which? reliability data
      maintenanceCost: 225  // £/year - Source: typical service contracts
    },
    // Ground Source Heat Pump data
    gshp: {
      cop: 4.5,  // More stable than ASHP
      copRange: { min: 4.0, max: 4.8 },
      flowTemp: 35,  // °C, ideal for underfloor heating
      capExBeforeGrant: 27000,  // £ average (horizontal trenches)
      boreholeCapEx: 40000,  // £ for borehole systems
      busGrant: 7500,  // £
      netCapEx: 19500,  // £ after grant (trenches)
      lifespan: 25,  // years (heat pump unit)
      groundLoopLifespan: 65,  // years (ground loops)
      maintenanceCost: 200,  // £/year
      spaceRequired: "large"  // Requires significant outdoor area
    },
    // Operating costs depend on electricity tariff
    electricityUnitCost: 0.2769,  // £/kWh (UK Q1 2026 price cap)
    gasUnitCost: 0.0593,  // £/kWh
    heatPumpTariff: 0.15,  // £/kWh for dedicated heat pump tariffs
    offPeakTariff: 0.07  // £/kWh for overnight charging
  },
  pressureRequirements: {
    combi: {
      minPressure: 1.5,
      minFlowRate: 14,
      goodPressure: 2.0,
      goodFlowRate: 18
    },
    storageCombi: {
      minPressure: 1.5,
      minFlowRate: 12,
      goodPressure: 2.0,
      goodFlowRate: 16
    },
    // Legacy flat structure for backward compatibility
    unvented: {
      minPressure: 1.5,
      minFlowRate: 12,
      goodPressure: 2.0,
      goodFlowRate: 16
    },
    mixergyUnvented: {
      minPressure: 0.5,  // Absolute operational minimum
      safePressure: 1.0, // Safe operational threshold (weighted risk below this)
      minFlowRate: 6,
      goodPressure: 2.0, // Optimal performance
      goodFlowRate: 30
    },
    mixergyOpen: {
      minPressure: 0.5,  // Absolute operational minimum
      safePressure: 1.0, // Safe operational threshold (weighted risk below this)
      minFlowRate: 6,
      goodPressure: 1.0,
      goodFlowRate: 10
    },
    openVented: {
      minPressure: 0.5,
      minFlowRate: 6,
      goodPressure: 1.0,
      goodFlowRate: 10
    },
    // New nested structure by strategy and variant
    byStrategyVariant: {
      unvented: {
        standard: {
          minPressure: 1.5,
          minFlowRate: 12,
          goodPressure: 2.0,
          goodFlowRate: 16
        },
        mixergy: {
          minPressure: 0.5,  // Absolute operational minimum
          safePressure: 1.0, // Safe operational threshold (weighted risk below this)
          minFlowRate: 6,
          goodPressure: 2.0, // Optimal performance
          goodFlowRate: 30
        }
      },
      openVented: {
        standard: {
          minPressure: 0.5,
          minFlowRate: 6,
          goodPressure: 1.0,
          goodFlowRate: 10
        },
        mixergy: {
          minPressure: 0.5,  // Absolute operational minimum
          safePressure: 1.0, // Safe operational threshold (weighted risk below this)
          minFlowRate: 6,
          goodPressure: 1.0,
          goodFlowRate: 10
        }
      }
    },
    heatPump: {
      // Heat pumps work with any mains pressure (sealed system)
      minPressure: 1.0,
      minFlowRate: 10,
      goodPressure: 1.5,
      goodFlowRate: 12
    }
  },
  // Heat battery (Sunamp) and thermal store data
  storage: {
    heatBattery: {
      sizeFactor: 0.25,  // 4x smaller than water cylinder
      lifespan: 50,  // years
      standingLoss: 0.3  // kWh/day (very low)
    },
    thermalStore: {
      standingLoss: 1.8,  // kWh/day
      multiSource: true,  // Can integrate multiple heat sources
      lifespan: 15  // years
    }
  },
  // Cylinder lifespan data (years) - supports both old string keys and new strategy+variant model
  cylinderLifespan: {
    heat_battery: 50,
    mixergy_unvented: 22,  // Legacy compatibility
    mixergy_open: 22,      // Legacy compatibility
    unvented: 25,  // 25-year warranty
    open_vented: 18,
    thermal_store: 15,
    storage_combi: 12,  // Integrated with boiler
    on_demand: 0  // No cylinder (combi boiler)
  },
  // Cylinder lifespan by strategy and variant
  cylinderLifespanByStrategy: {
    open_vented: { standard: 18, mixergy: 22 },
    unvented: { standard: 25, mixergy: 22 },
    heat_battery: { standard: 50 },
    thermal_store: { standard: 15 },
    storage_combi: { standard: 12 },
    on_demand: { standard: 0 }
  }
};

// Heat loss calculation defaults (W/m² by property age band)
const heatLossIntensityByAge: Record<string, number> = {
  "pre_1919": 140,
  "1919_1944": 120,
  "1945_1964": 100,
  "1965_1980": 85,
  "1981_2000": 70,
  "2001_2010": 55,
  "2011_plus": 45,
  "unknown": 100
};

// Assumed floor areas by property type (m²)
const assumedFloorAreaByType: Record<string, number> = {
  "flat": 60,
  "flat/bungalow": 60,
  "apartment": 60,
  "maisonette": 70,
  "mid_terrace": 85,
  "mid-terrace": 85,
  "terraced": 85,
  "end_terrace": 95,
  "end-terrace": 95,
  "semi": 110,
  "semi_detached": 110,
  "semi-detached": 110,
  "detached": 140,
  "bungalow": 95,
  "semi_detached_bungalow": 110,
  "semi-detached-bungalow": 110,
  "unknown": 100
};

/**
 * Clamp a number to a specified range
 */
function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Compute value modifier based on annualized cost
 * Cheaper systems get positive modifier, more expensive get negative
 * @param annualizedCost - The annualized cost of this option
 * @param minCost - Minimum annualized cost across all options
 * @param maxCost - Maximum annualized cost across all options
 * @param strengthPct - User-selected strength (0-25%)
 * @returns Score modifier (can be positive or negative)
 */
function computeValueModifier(
  annualizedCost: number,
  minCost: number,
  maxCost: number,
  strengthPct: number
): number {
  // Normalize so cheaper = higher modifier.
  // If all same cost, modifier = 0.
  if (!isFinite(annualizedCost) || maxCost <= minCost) return 0;

  const strength = clamp(strengthPct, 0, 25) / 100;      // 0.00–0.25
  const t = (annualizedCost - minCost) / (maxCost - minCost); // 0 (cheapest) → 1 (priciest)
  const valueScore = 1 - clamp(t, 0, 1);                 // 1 (best) → 0 (worst)

  // Centered around 0 so mid-cost ~0 modifier.
  // Range approx: [-strength/2, +strength/2] of BASE SCORE RANGE.
  // Score range is 0–10, so we scale by 10.
  const BASE_SCORE_RANGE = 10;

  return (valueScore - 0.5) * (strength * BASE_SCORE_RANGE);
}

/**
 * Get appropriate SCOP value for heat pump based on emitter configuration
 * Returns midpoint of range for calculations
 * 
 * This dynamic calculation is used throughout the engine for accurate cost estimates.
 * Note: systemData.heatPump.ashp.scop is a static reference value (3.65) used for
 * simple baseline comparisons. Always prefer this function for actual calculations.
 */
function getEffectiveScop(
  emitterSuitability?: string,
  emitterType?: string,
  flowTemp: number = 55
): number {
  const lowTempConfirmed = isLowTempConfirmed(emitterSuitability, emitterType);
  
  if (lowTempConfirmed) {
    // Low-temp emitters confirmed: use optimistic range
    const range = getScopWithUpgrades();
    return (range.min + range.max) / 2; // 4.25
  } else {
    // Not confirmed: use conservative range for current config
    const range = getScopAsIs(flowTemp);
    return (range.min + range.max) / 2; // e.g., 3.1 for 55°C
  }
}

/**
 * Calculate real-world running costs per kWh of heat output
 * Accounts for efficiency and fuel costs
 */
function calculateRunningCostPerKWh(
  systemType: string, 
  hasHeatPumpTariff: boolean = false,
  emitterSuitability?: string,
  emitterType?: string,
  flowTemp: number = 55
): {
  costPerKWh: number;
  description: string;
} {
  const gasEfficiency = 0.93; // 93% ERP-rated condensing gas boiler
  
  const gasCost = systemData.heatPump.gasUnitCost; // £0.0593/kWh
  const electricityCost = hasHeatPumpTariff
    ? systemData.heatPump.heatPumpTariff  // £0.15/kWh (dedicated HP tariff)
    : systemData.heatPump.electricityUnitCost; // £0.2769/kWh (standard tariff)

  if (systemType === "air_source_hp" || systemType === "ground_source_hp") {
    // Use conditional SCOP based on emitter configuration
    const heatPumpSCOP = getEffectiveScop(emitterSuitability, emitterType, flowTemp);
    const electricityNeeded = 1 / heatPumpSCOP; // kWh electricity per kWh heat
    const cost = electricityNeeded * electricityCost;

    if (hasHeatPumpTariff) {
      return {
        costPerKWh: cost,
        description: `£${cost.toFixed(3)}/kWh heat (SCOP ${heatPumpSCOP.toFixed(1)}, with dedicated heat pump tariff at £${electricityCost}/kWh)`
      };
    } else {
      return {
        costPerKWh: cost,
        description: `£${cost.toFixed(3)}/kWh heat (SCOP ${heatPumpSCOP.toFixed(1)}, standard tariff at £${electricityCost}/kWh - electricity is 4.7× gas price)`
      };
    }
  } else {
    // Gas boiler (combi, storage combi, system, regular)
    const gasNeeded = 1 / gasEfficiency; // kWh gas per kWh heat
    const cost = gasNeeded * gasCost;
    return {
      costPerKWh: cost,
      description: `£${cost.toFixed(3)}/kWh heat (gas at £${gasCost}/kWh)`
    };
  }
}

/**
 * Calculate heat loss from property characteristics
 * Uses known heat loss if provided, otherwise calculates from property type and age
 * Returns heat loss in kW and assumptions made
 */
function calculateHeatLoss(input: SystemRecInput): { 
  heatLossKw: number; 
  useKnownHeatLoss: boolean;
  assumptions: string[];
  confidenceBoost: number;
} {
  const assumptions: string[] = [];
  let confidenceBoost = 0;
  
  // Priority 1: Known heat loss (explicitly provided)
  if (input.knownHeatLossKw != null && input.knownHeatLossKw > 0) {
    assumptions.push(`Using provided heat loss: ${input.knownHeatLossKw.toFixed(1)} kW`);
    return {
      heatLossKw: input.knownHeatLossKw,
      useKnownHeatLoss: true,
      assumptions,
      confidenceBoost: 0.25  // +25% confidence boost for known heat loss
    };
  }
  
  // Priority 2: Legacy heatLossKw field
  if (input.heatLossKw != null && input.heatLossKw > 0) {
    assumptions.push(`Using provided heat loss: ${input.heatLossKw.toFixed(1)} kW`);
    return {
      heatLossKw: input.heatLossKw,
      useKnownHeatLoss: true,
      assumptions,
      confidenceBoost: 0.25  // +25% confidence boost for known heat loss
    };
  }
  
  // Priority 3: Calculate from property type and age
  const propertyType = input.propertyType || "unknown";
  const ageBand = input.propertyAgeBand || "unknown";
  
  // Get W/m² for this age band
  const wPerM2 = heatLossIntensityByAge[ageBand] || heatLossIntensityByAge["unknown"];
  
  // Get assumed floor area for this property type
  const areaM2 = assumedFloorAreaByType[propertyType] || assumedFloorAreaByType["unknown"];
  
  // Calculate heat loss: (W/m² * area) / 1000 = kW
  const heatLossKw = (wPerM2 * areaM2) / 1000;
  
  assumptions.push(`Heat loss inferred from property type (${propertyType}, ${areaM2}m²) and age (${ageBand}, ${wPerM2}W/m²): ${heatLossKw.toFixed(1)} kW. Note: This is not a room-by-room calculation. A measured heat loss survey may change system sizing recommendations.`);
  
  return {
    heatLossKw,
    useKnownHeatLoss: false,
    assumptions,
    confidenceBoost: 0  // No confidence boost for calculated heat loss
  };
}

/**
 * Estimate annual useful heat demand based on property characteristics
 * Returns estimated kWh/year and notes explaining the calculation
 */
function estimateAnnualUsefulHeatKwh(input: SystemRecInput): { value: number; notes: string[] } {
  // SIMPLE + defensible starter estimate (replace later with your heat-loss module)
  // Base by propertyType (kWh/yr), nudged by bedrooms + insulationLevel
  const type = input.propertyType || "semi";
  const bedrooms = input.bedrooms ?? 3;
  const insulation = input.insulationLevel || "unknown";

  const baseByType: Record<string, number> = {
    "flat/bungalow": 9000,
    "terraced": 11000,
    "semi": 12500,
    "detached": 15500
  };

  let kwh = baseByType[type] ?? 12500;

  // Bedrooms nudges occupancy/area proxy
  kwh += (bedrooms - 3) * 1200;

  // Insulation factor (very rough)
  const mult = insulation === "high" ? 0.85
            : insulation === "medium" ? 0.95
            : insulation === "poor" ? 1.15
            : 1.0;

  kwh = Math.max(6000, Math.round(kwh * mult));

  const notes = [
    `Annual useful heat demand estimated at ~${kwh.toLocaleString()} kWh/yr (propertyType=${type}, bedrooms=${bedrooms}, insulation=${insulation}).`,
    `For accuracy, enter your own annual demand (from bills, EPC, or heat-loss).`
  ];

  return { value: kwh, notes };
}

/**
 * Get system lifespan in years based on boiler and water system types
 * Returns the minimum of boiler and cylinder lifespans, as the system
 * will need replacement when either component reaches end of life
 */
function getSystemLifespanYears(boiler: string, water: string): number {
  // Boiler lifespans (using average of longevity profile ranges)
  let boilerLifespan: number;
  if (boiler === "ground_source_hp") {
    boilerLifespan = 23.5;  // Average of 22-25 years
  } else if (boiler === "air_source_hp") {
    boilerLifespan = 20;    // Average of 18-22 years
  } else if (boiler === "storage_combi") {
    boilerLifespan = 12;    // Average of 11-13 years
  } else if (boiler === "combi") {
    boilerLifespan = 11;    // Average of 10-12 years
  } else if (boiler === "system") {
    boilerLifespan = 13.5;  // Average of 12-15 years
  } else if (boiler === "regular") {
    boilerLifespan = 17.5;  // Average of 15-20 years
  } else {
    boilerLifespan = 15;
  }

  // Cylinder lifespans
  const cylinderLifespan = systemData.cylinderLifespan[water as keyof typeof systemData.cylinderLifespan];

  // If no cylinder (combi/on_demand), return boiler lifespan
  if (!cylinderLifespan || cylinderLifespan === 0) {
    return boilerLifespan;
  }

  // Return minimum of boiler and cylinder lifespans
  // (system needs replacement when either component fails)
  return Math.min(boilerLifespan, cylinderLifespan);
}

/**
 * Get default annual maintenance cost based on boiler type
 */
function getDefaultAnnualMaintenanceGBP(boiler: string): number {
  if (boiler === "ground_source_hp") return 200;
  if (boiler === "air_source_hp") return 225;
  // Gas service baseline
  return 120;
}

/**
 * Compute LCOO breakdown for a single option
 * Returns null if no cost data provided
 */
function computeLcooForOption(
  optionId: string,
  boiler: string,
  water: string,
  input: SystemRecInput,
  lcooInput?: LcooInput
): LcooBreakdown | null {
  if (!lcooInput) return null;

  const periodYears = Math.max(1, Math.round(lcooInput.periodYears || 20));
  const perOpt = lcooInput.perOption?.[optionId];

  // If user hasn't provided any pricing for this option, don't fabricate totals
  if (!perOpt) return null;

  const assumptions: string[] = [];
  const warnings: string[] = [];

  // Annual demand
  let annualUsefulHeatKwh = lcooInput.annualUsefulHeatKwh;
  if (!annualUsefulHeatKwh || annualUsefulHeatKwh <= 0) {
    const est = estimateAnnualUsefulHeatKwh(input);
    annualUsefulHeatKwh = est.value;
    assumptions.push(...est.notes);
  }

  // Cost per kWh of DELIVERED heat (accounting for system efficiency)
  const usesElec = boiler === "air_source_hp" || boiler === "ground_source_hp";
  // Check if using Mixergy variant (supports both old string format and new strategy+variant)
  const waterObj = typeof water === 'object' && water !== null && 'variant' in water ? water as { strategy: string; variant: string } : null;
  const isMixergy = water === "mixergy_unvented" || water === "mixergy_open" || (waterObj && waterObj.variant === 'mixergy');
  
  let fuelKwhNeeded: number;
  let fuelCostPerKwh: number;
  let efficiencyNote: string;
  
  if (usesElec) {
    // Heat pumps: Use conditional SCOP based on emitter configuration
    const emitterSuitability = input.emitters?.lowTempSuitability;
    const emitterType = input.emitters?.type;
    const flowTemp = 55; // Default assumption for cost estimation
    const scop = getEffectiveScop(emitterSuitability, emitterType, flowTemp);
    
    fuelKwhNeeded = 1 / scop; // kWh elec per kWh heat
    fuelCostPerKwh = lcooInput.tariffs.elecPPerKwh / 100;
    efficiencyNote = `Heat pump SCOP ${scop.toFixed(1)} (${(scop * 100).toFixed(0)}% efficiency)`;
    
    // Add note if SCOP is based on assumptions
    if (!isLowTempConfirmed(emitterSuitability, emitterType)) {
      assumptions.push(`SCOP based on current emitters; could improve to 4.0-4.5 with low-temp upgrades`);
    }
  } else {
    // Gas boilers: ~94% efficient means 1.06 kWh gas per 1 kWh heat
    const efficiency = 0.93; // 93% ERP-rated condensing gas boiler
    fuelKwhNeeded = 1 / efficiency; // ~1.06 kWh gas per kWh heat
    fuelCostPerKwh = lcooInput.tariffs.gasPPerKwh / 100;
    efficiencyNote = `Gas boiler ${(efficiency * 100).toFixed(0)}% efficiency`;
  }
  
  const costPerKwhDeliveredHeat = fuelKwhNeeded * fuelCostPerKwh;
  
  // === CYCLING LOSS MODEL ===
  // Determine cycling risk and apply load shaping benefit for Mixergy
  
  // Constants for boiler sizing estimation
  const TYPICAL_BOILER_SIZING_INCREMENT_KW = 5; // Boilers sized in 5 kW increments
  const MIN_BOILER_SIZING_FACTOR = 1.2; // Minimum 20% oversizing for DHW capacity
  
  // 1. Determine cycling risk from boiler oversizing
  let cyclingRisk: CyclingRisk = 'low';
  let cyclingLossPenalty = CYCLING_LOSS_PENALTY_BY_RISK.low;
  let oversizingRatio: number | undefined;
  
  const heatLossKw = input.knownHeatLossKw || input.heatLossKw;
  
  // For gas boilers, estimate cycling risk
  if (!usesElec && boiler !== 'combi') {
    // Try to calculate from known heat loss
    if (heatLossKw && heatLossKw > 0) {
      // Estimate typical boiler size for the heat loss
      // System/regular boilers typically sized at 1.1-1.5x heat loss for DHW capacity
      // Round up to nearest increment (typically 5 kW)
      const typicalBoilerSizeKw = Math.ceil(heatLossKw / TYPICAL_BOILER_SIZING_INCREMENT_KW) * TYPICAL_BOILER_SIZING_INCREMENT_KW;
      const assumedBoilerOutputKw = Math.max(typicalBoilerSizeKw, heatLossKw * MIN_BOILER_SIZING_FACTOR);
      
      oversizingRatio = assumedBoilerOutputKw / heatLossKw;
      
      // Use determineCyclingRisk function
      const cyclingData = determineCyclingRisk(oversizingRatio);
      cyclingRisk = cyclingData.risk;
      cyclingLossPenalty = cyclingData.penalty;
    } else {
      // Fallback: infer from property characteristics
      const bedrooms = input.bedrooms || 3;
      const propertyType = input.propertyType || 'semi';
      
      // Larger/older properties tend to have more oversized boilers
      if (bedrooms >= 4 || propertyType === 'detached') {
        cyclingRisk = 'medium';
        cyclingLossPenalty = CYCLING_LOSS_PENALTY_BY_RISK.medium;
      } else {
        cyclingRisk = 'low';
        cyclingLossPenalty = CYCLING_LOSS_PENALTY_BY_RISK.low;
      }
    }
  }
  
  // 2. Calculate DHW demand and headroom for load shaping
  const hasStoredDHW = water !== 'on_demand' && boiler !== 'combi';
  const dhwUsefulKwhPerDay = annualUsefulHeatKwh * 0.20 / 365; // 20% of annual for DHW, per day
  const dhwHeadroomKwh = hasStoredDHW ? 2.0 : 0; // Assume 2 kWh headroom for stored DHW systems
  
  // 3. Apply load shaping benefit (only for Mixergy with stored DHW on gas boilers)
  let adjustedCyclingPenalty = cyclingLossPenalty;
  let loadShapingEvidence = '';
  let shiftKwhPerDay = 0;
  
  if (isMixergy && !usesElec && hasStoredDHW && cyclingLossPenalty > 0) {
    // Use calculateLoadShapingBenefit function
    const loadShapingResult = calculateLoadShapingBenefit({
      baseCyclingPenalty: cyclingLossPenalty,
      dhwUsefulKwhPerDay,
      dhwHeadroomKwh,
      isMixergy: true,
      hasStoredDHW: true,
    });
    
    adjustedCyclingPenalty = loadShapingResult.adjustedPenalty;
    shiftKwhPerDay = loadShapingResult.shiftKwhPerDay;
    
    if (loadShapingResult.penaltyReduction > 0) {
      const reductionPct = ((loadShapingResult.penaltyReduction / cyclingLossPenalty) * 100).toFixed(0);
      loadShapingEvidence = ` Mixergy load-shaping reduces short-cycling penalty from ${(cyclingLossPenalty * 100).toFixed(0)}% → ${(adjustedCyclingPenalty * 100).toFixed(0)}% (shifts ${shiftKwhPerDay.toFixed(1)} kWh/day into useful DHW storage).`;
    }
  }
  
  // 4. Calculate fuel consumption with cycling penalty
  // Heating fuel = useful heat / efficiency * (1 + cycling penalty)
  // DHW fuel = DHW useful / efficiency (no cycling penalty for DHW-only runs)
  const spaceHeatingKwh = annualUsefulHeatKwh * 0.80; // 80% space heating
  const dhwKwh = annualUsefulHeatKwh * 0.20; // 20% DHW
  
  const heatingFuelKwh = (spaceHeatingKwh / (usesElec ? 4.5 : 0.93)) * (1 + adjustedCyclingPenalty);
  const dhwFuelKwh = dhwKwh / (usesElec ? 4.5 : 0.93);
  const totalFuelKwh = heatingFuelKwh + dhwFuelKwh;
  
  let annualRunningGBP = totalFuelKwh * fuelCostPerKwh;
  
  // 5. Add evidence to assumptions
  if (!usesElec && adjustedCyclingPenalty !== cyclingLossPenalty) {
    assumptions.push(`Running cost calculated using ${efficiencyNote}.${loadShapingEvidence}`);
  } else if (!usesElec && cyclingLossPenalty > 0) {
    assumptions.push(`Running cost calculated using ${efficiencyNote}. Cycling losses: ${(cyclingLossPenalty * 100).toFixed(0)}% (${cyclingRisk} risk).`);
  } else {
    assumptions.push(`Running cost calculated using ${efficiencyNote}.`);
  }

  // Grants
  const isHeatPump = boiler === "air_source_hp" || boiler === "ground_source_hp";
  const busGrant = isHeatPump && perOpt.applyBUSGrant
    ? Math.max(0, perOpt.busGrantAmountGBP ?? 7500)
    : 0;

  if (perOpt.applyBUSGrant && !isHeatPump) {
    warnings.push("BUS grant toggle was set but this option is not a heat pump; grant ignored.");
  }

  let upfrontGrossGBP = Math.max(0, perOpt.upfrontGrossGBP || 0);

  // Smart control notes for gas boiler systems
  // NOTE: We do NOT add thermostat cost to capex. Controls are about behaviour, not equipment cost.
  // The efficiency benefit of load-compensating controls is already factored into running costs.
  const isGasBoiler = boiler === "combi" || boiler === "storage_combi" || boiler === "system" || boiler === "regular";
  const thermostatPreference = input.thermostatPreference;

  if (isGasBoiler && thermostatPreference?.includeThermostat) {
    // Add behavioral notes, NOT cost notes
    if (thermostatPreference.preferredControlType === 'smart_load_comp') {
      // Only load-compensating controls get efficiency uplift
      assumptions.push('Load-compensating controls can reduce fuel use by 15-22% through boiler modulation.');
    } else if (thermostatPreference.preferredControlType === 'smart_on_off') {
      // Relay-only smart stats do NOT get efficiency uplift
      warnings.push('Relay-only smart thermostats cannot modulate boiler output. For efficiency benefits, use load-compensating controls.');
    }
    // No cost added to upfrontGrossGBP - controls are advisory, not part of system cost comparison
  }

  // Handle retained existing cylinder
  const retainCylinder = perOpt.retainExistingCylinder ?? false;
  if (retainCylinder) {
    const cylinderCost = perOpt.cylinderCostGBP ?? 0;
    if (cylinderCost > 0) {
      upfrontGrossGBP = Math.max(0, upfrontGrossGBP - cylinderCost);
      assumptions.push(`Retaining existing cylinder - reduced upfront cost by £${cylinderCost.toFixed(0)}.`);
    } else {
      assumptions.push("Retaining existing cylinder (cost not separately specified).");
    }

    const cylinderAge = perOpt.existingCylinderAgeYears ?? 0;
    if (cylinderAge > 0) {
      assumptions.push(`Existing cylinder age: ${cylinderAge} years.`);
    } else {
      warnings.push("Retaining existing cylinder but age not specified - assuming cylinder is in good condition.");
    }
  }

  const grantsGBP = perOpt.upfrontIsBeforeGrants ? Math.min(busGrant, upfrontGrossGBP) : 0;
  const upfrontNetGBP = perOpt.upfrontIsBeforeGrants
    ? Math.max(0, upfrontGrossGBP - grantsGBP)
    : upfrontGrossGBP;

  if (perOpt.upfrontIsBeforeGrants && isHeatPump && !perOpt.applyBUSGrant) {
    assumptions.push("Upfront price marked as 'before grants', but BUS grant not applied.");
  }

  // Maintenance
  const annualMaintenanceGBP = Math.max(
    0,
    perOpt.annualMaintenanceGBP ?? getDefaultAnnualMaintenanceGBP(boiler)
  );

  // Replacements
  let systemLifespanYears = getSystemLifespanYears(boiler, water);

  // If retaining existing cylinder, calculate lifespan and replacement costs differently
  let cylinderReplacementCostGBP = 0;
  if (retainCylinder) {
    // Get boiler-only lifespan (ignore cylinder)
    const boilerLifespan = getSystemLifespanYears(boiler, "on_demand");
    systemLifespanYears = boilerLifespan;

    // Calculate cylinder replacement if needed during analysis period
    const cylinderAge = perOpt.existingCylinderAgeYears ?? 0;
    const cylinderLifespan = systemData.cylinderLifespan[water as keyof typeof systemData.cylinderLifespan] || 18;
    const cylinderRemainingLife = Math.max(0, cylinderLifespan - cylinderAge);

    if (cylinderRemainingLife < periodYears) {
      // Cylinder will need replacement during analysis period
      const cylinderCost = perOpt.cylinderCostGBP ?? 0;
      if (cylinderCost > 0) {
        cylinderReplacementCostGBP = cylinderCost;
        assumptions.push(
          `Existing cylinder (age ${cylinderAge}y) will need replacement within analysis period (${cylinderRemainingLife}y remaining of ${cylinderLifespan}y lifespan). Replacement cost: £${cylinderCost.toFixed(0)}.`
        );
      } else {
        warnings.push(
          `Existing cylinder (age ${cylinderAge}y) may need replacement within analysis period (${cylinderRemainingLife}y remaining), but cylinder cost not specified.`
        );
      }
    } else {
      assumptions.push(
        `Existing cylinder (age ${cylinderAge}y) has ${cylinderRemainingLife}y remaining life - sufficient for ${periodYears}y analysis period.`
      );
    }
  }

  // Calculate replacements: if lifespan >= analysis period, no replacements needed
  const replacementCount = systemLifespanYears >= periodYears ? 0 : Math.floor(periodYears / systemLifespanYears);
  const replacementFactor = Math.max(0, perOpt.replacementCostFactor ?? 1.0);
  const replacementCostTotalGBP = (replacementCount * (upfrontNetGBP * replacementFactor)) + cylinderReplacementCostGBP;

  if (replacementCount > 0 || cylinderReplacementCostGBP > 0) {
    if (!retainCylinder) {
      assumptions.push(
        `Replacement model: ${replacementCount} full system replacement(s) within ${periodYears} years (lifespan ~${systemLifespanYears}y).`
      );
    } else {
      assumptions.push(
        `Replacement model: ${replacementCount} boiler replacement(s) within ${periodYears} years (boiler lifespan ~${systemLifespanYears}y).`
      );
    }
  }

  const totalCostGBP =
    upfrontNetGBP +
    (annualRunningGBP * periodYears) +
    (annualMaintenanceGBP * periodYears) +
    replacementCostTotalGBP;

  const annualizedCostGBP = totalCostGBP / periodYears;

  return {
    periodYears,
    upfrontGrossGBP,
    grantsGBP,
    upfrontNetGBP,
    annualUsefulHeatKwh,
    costPerKwhGBP: costPerKwhDeliveredHeat,
    annualRunningGBP,
    annualMaintenanceGBP,
    systemLifespanYears,
    replacementCount,
    replacementCostTotalGBP,
    totalCostGBP,
    annualizedCostGBP,
    assumptions,
    warnings
  };
}

/**
 * Evaluate if a system configuration is fundamentally unsuitable
 * Returns exclusion status, reason, and score penalty
 */
function evaluateSystemSuitability({
  boiler,
  water,
  bathrooms,
  occupants,
  flowRate,
  mainsPressure,
  coldMainsCertainty,
  urgency,
  costPriority,
  budgetVsGreen,
  budgetVsLongevity,
  disruptionTolerance,
  acceptsVisiblePipework,
  wallSpaceForOversizedRads,
  heatEmitterType,
  insulationLevel,
  plannedInsulationUpgrades
}: {
  boiler: string;
  water: string;
  bathrooms: number;
  occupants: number;
  flowRate: number;
  mainsPressure: number;
  coldMainsCertainty: 'confirmed' | 'assumed' | 'unknown';
  urgency: string;
  costPriority: string;
  budgetVsGreen: string;
  budgetVsLongevity: string;
  disruptionTolerance: string;
  acceptsVisiblePipework: boolean;
  wallSpaceForOversizedRads: boolean;
  heatEmitterType: string;
  insulationLevel: string;
  plannedInsulationUpgrades: boolean;
}): {
  excluded: boolean;
  reason: string;
  penaltyScore: number;
} {
  const hasStoredWater = hasStoredWaterSystem(water);
  const isCombi = boiler === "combi";
  const isStorageCombi = boiler === "storage_combi";
  const isASHP = boiler === "air_source_hp";
  const isGSHP = boiler === "ground_source_hp";
  const isHeatPump = isASHP || isGSHP;
  const isUnvented = water === "unvented" || water === "mixergy_unvented";
  const normalizedDisruption = disruptionTolerance === "one_room_only" || disruptionTolerance === "minimal"
    ? "low"
    : disruptionTolerance === "two_three_rooms" || disruptionTolerance === "moderate"
      ? "medium"
      : "high";

  // CRITICAL: Block combi and unvented recommendations if cold mains not assessed
  // This prevents making recommendations based on assumed values that may be wrong
  if (coldMainsCertainty !== 'confirmed') {
    if (isCombi) {
      return {
        excluded: true,
        reason: `❌ Cannot confirm suitability: Cold mains capacity not assessed. Combi boilers require minimum 1.5 bar pressure and 14 L/min flow rate. Measure cold mains before recommending this option.`,
        penaltyScore: -100
      };
    }
    
    if (isUnvented) {
      return {
        excluded: true,
        reason: `❌ Cannot confirm suitability: Cold mains capacity not assessed. Unvented cylinders require minimum 2.0 bar pressure and 12 L/min flow rate for reliable operation. Measure cold mains before recommending this option.`,
        penaltyScore: -100
      };
    }
  }

  // Critical exclusion: Stored hot water requires regular/system boiler (not combi)
  if (hasStoredWater && (isCombi || isStorageCombi)) {
    return {
      excluded: true,
      reason: "❌ Not suitable: Stored hot water systems need a regular or system boiler (combis are on-demand only).",
      penaltyScore: -100
    };
  }

  // Critical exclusion: On-demand hot water requires combi/storage combi
  if (!hasStoredWater && water === "on_demand" && !(isCombi || isStorageCombi)) {
    return {
      excluded: true,
      reason: "❌ Not suitable: On-demand hot water requires a combi or storage combi boiler.",
      penaltyScore: -100
    };
  }

  // Critical exclusion: 3+ bathrooms + standard combi
  if (isCombi && bathrooms >= 3) {
    const simultaneousFlowNeeded = bathrooms * 12; // Assume 12 L/min per shower
    const combiMaxFlow = 12; // Typical combi max flow
    return {
      excluded: true,
      reason: `❌ Not suitable: ${bathrooms} bathrooms need ~${simultaneousFlowNeeded} L/min for simultaneous showers, but standard combis only supply ~${combiMaxFlow} L/min. Physics makes this impossible - you'll get cold water when two showers run simultaneously.`,
      penaltyScore: -100
    };
  }

  // Critical exclusion: Low flow rate + combi
  if (isCombi && flowRate < 14) {
    return {
      excluded: true,
      reason: `❌ Not suitable: Water pressure test shows ${flowRate} L/min flow rate, which is below the minimum ${14} L/min needed for a combi boiler. This will result in weak water pressure and cold showers during use.`,
      penaltyScore: -100
    };
  }

  // Critical exclusion: Low pressure + combi
  if (isCombi && mainsPressure < 1.5) {
    return {
      excluded: true,
      reason: `❌ Not suitable: Water pressure test shows ${mainsPressure} bar, which is below the minimum ${1.5} bar needed for a combi boiler. The combi will not operate reliably with this pressure.`,
      penaltyScore: -100
    };
  }

  // Critical exclusion: 5+ occupants + standard combi
  if (isCombi && occupants >= 5) {
    return {
      excluded: true,
      reason: `❌ Not suitable: ${occupants} people in the household means someone will always be waiting for hot water. Standard combis can't keep up with this demand - you need stored hot water (cylinder) for reliable service.`,
      penaltyScore: -100
    };
  }

  // Critical exclusion: Emergency urgency + heat pump
  if ((isASHP || isGSHP) && urgency === "emergency") {
    return {
      excluded: true,
      reason: `❌ Not suitable: Heat pump installation requires 4-8 weeks (surveys, grants, radiator upgrades), but you need emergency replacement within 1-2 days. Heat pumps are excellent long-term but impossible for urgent needs.`,
      penaltyScore: -100
    };
  }

  // Critical exclusion: Visible pipework not acceptable with low disruption tolerance
  if (isHeatPump && !acceptsVisiblePipework && normalizedDisruption === "low") {
    return {
      excluded: true,
      reason: "❌ Not suitable: Heat pump retrofit requires visible surface-mounted pipework, and you indicated this is unacceptable with low disruption tolerance.",
      penaltyScore: -100
    };
  }

  // Critical exclusion: No space for oversized radiators (unless already UFH)
  if (isHeatPump && !wallSpaceForOversizedRads && heatEmitterType !== "underfloor_heating") {
    return {
      excluded: true,
      reason: "❌ Not suitable: Standard heat pumps need radiators ~2.5× larger at low flow temperatures. With limited wall space, consider a hybrid or high-temperature heat pump instead.",
      penaltyScore: -100
    };
  }

  // Critical exclusion: Poor insulation without upgrades planned (ASHP running costs too high)
  if (isASHP && insulationLevel === "poor" && !plannedInsulationUpgrades) {
    return {
      excluded: true,
      reason: "❌ Not suitable: EPC D or below with no planned insulation upgrades makes ASHP running costs uncompetitive on standard electricity tariffs.",
      penaltyScore: -100
    };
  }

  // Semi-critical: Storage combi with 3 bathrooms
  if (isStorageCombi && bathrooms >= 3) {
    const simultaneousFlowNeeded = bathrooms * 12;
    const storageTankSize = 46; // L, typical storage combi
    const drawDuration = 10; // minutes
    const totalDemand = simultaneousFlowNeeded * drawDuration;

    if (totalDemand > storageTankSize * 2) { // Tank can't keep up even with reheating
      return {
        excluded: true,
        reason: `❌ Not suitable: ${bathrooms} bathrooms with simultaneous use (~${simultaneousFlowNeeded} L/min for ${drawDuration} min = ${totalDemand}L) exceeds storage combi's ${storageTankSize}L tank capacity. You'll experience cold water mid-shower when multiple people use it.`,
        penaltyScore: -90
      };
    }
  }

  // Semi-critical: Low flow + storage combi
  if (isStorageCombi && flowRate < 12) {
    return {
      excluded: true,
      reason: `❌ Not suitable: Water pressure test shows ${flowRate} L/min flow rate, below the minimum ${12} L/min for storage combi. Flow will be inadequate even with the integrated tank.`,
      penaltyScore: -90
    };
  }

  // Budget-based exclusions: Heat pumps are excellent long-term but have high upfront costs
  // Only exclude if user has STRONG budget constraints (both upfront cost priority AND budget-focused)
  if (isHeatPump && costPriority === "upfront_cost" && budgetVsGreen === "budget_focused") {
    const ashpCost = isASHP ? "£3,500-£7,000" : "£19,500+";
    const gasCost = "£2,000-£4,000";
    return {
      excluded: true,
      reason: `❌ Not suitable for strict budget: Heat pumps cost ${ashpCost} after grants vs ${gasCost} for gas boilers. With your upfront cost priority and budget focus, a gas boiler is more appropriate. Heat pumps offer long-term savings but require significant initial investment.`,
      penaltyScore: -100
    };
  }

  // Also exclude GSHP if budget-focused on longevity (GSHP is the most expensive option)
  if (isGSHP && costPriority === "upfront_cost" && budgetVsLongevity === "budget_focused") {
    return {
      excluded: true,
      reason: `❌ Not suitable for strict budget: Ground source heat pumps cost £19,500+ after grants (£27k before), which conflicts with your upfront cost priority and budget focus. Consider air source heat pump (£3.5k) or gas boiler (£2-4k) instead.`,
      penaltyScore: -100
    };
  }

  // Semi-critical: Unvented cylinder with low mains pressure
  // Unvented systems require good mains pressure to function well
  // Standard unvented: min 1.5 bar / 12 L/min, good 2.0 bar / 16 L/min
  // Mixergy can work at lower pressures but shouldn't be primary if marginal
  const isUnventedWater = water === "unvented" || water === "mixergy_unvented";
  const isMixergyWater = water === "mixergy_unvented" || water === "mixergy_open";

  if (isUnventedWater && !isMixergyWater) {
    // Standard unvented - hard exclusion at <2.0 bar or <12 L/min
    // Performance requires adequate pressure for proper unvented operation
    if (mainsPressure < 2.0 || flowRate < 12) {
      return {
        excluded: true,
        reason: `❌ Not suitable: Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min. Unvented cylinders require minimum 2.0 bar and 12 L/min flow rate for adequate performance. Mains pressure/flow too low for reliable unvented operation. Consider vented (open vented) storage or improving your water supply.`,
        penaltyScore: -1000
      };
    }
    // Below "optimal" threshold - still works but apply penalty
    if (mainsPressure < 2.5 || flowRate < 16) {
      return {
        excluded: false,
        reason: `⚠ Marginal: Water pressure (${mainsPressure} bar / ${flowRate} L/min) is adequate but below optimal for unvented (best: ≥2.5 bar / 16 L/min). Performance may be reduced during peak demand.`,
        penaltyScore: -25
      };
    }
  }

  if (isMixergyWater) {
    // Mixergy unvented - hard exclusion at <2.0 bar or <12 L/min
    // Mixergy is more forgiving than standard but still needs adequate pressure
    if (mainsPressure < 2.0 || flowRate < 12) {
      return {
        excluded: true,
        reason: `❌ Not suitable: Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min. Mixergy unvented requires minimum 2.0 bar and 12 L/min flow rate. Mains pressure/flow too low for reliable unvented performance. Consider vented storage or pressure improvements.`,
        penaltyScore: -1000
      };
    }
    // Below optimal threshold - apply penalty to push vented options above it
    if (mainsPressure < 2.5 || flowRate < 16) {
      return {
        excluded: false,
        reason: `⚠ Marginal: Water pressure (${mainsPressure} bar / ${flowRate} L/min) is below optimal for Mixergy unvented (best: ≥2.5 bar / 30 L/min). System will function, but performance may be reduced during high-draw periods. Consider vented storage or pressure improvements.`,
        penaltyScore: -40  // Strong penalty to push vented options above it
      };
    }
  }

  // No exclusion - option is suitable
  return {
    excluded: false,
    reason: "",
    penaltyScore: 0
  };
}

/**
 * Check if a water system configuration includes stored hot water
 */
function hasStoredWaterSystem(water: string): boolean {
  // Extract strategy if water is in new format (object with strategy property)
  const waterObj = typeof water === 'object' && water !== null && 'strategy' in water ? water as { strategy: string } : null;
  const strategy = waterObj ? waterObj.strategy : water;
  
  return strategy === "unvented" || 
         strategy === "open_vented" || 
         water === "mixergy_unvented" ||  // Legacy compatibility
         water === "mixergy_open" ||      // Legacy compatibility
         strategy === "thermal_store" || 
         strategy === "heat_battery";
}

/**
 * Evaluate future upgrade positioning of a system configuration
 * Returns score adjustment and explanation text
 */
function evaluateFutureUpgradePositioning({
  currentBoiler,
  newBoiler,
  newWater
}: {
  currentBoiler: string;
  newBoiler: string;
  newWater: string;
}): {
  upgradeScore: number;
  upgradeText: string;
} {
  const isCombi = newBoiler === "combi";
  const isStorageCombi = newBoiler === "storage_combi";
  const isRegular = newBoiler === "regular";
  const isSystem = newBoiler === "system";
  const isHeatPump = newBoiler === "air_source_hp" || newBoiler === "ground_source_hp";
  const hasStoredWater = hasStoredWaterSystem(newWater);
  
  let upgradeScore = 0;
  let upgradeText = "";

  // ✅ Better positioned for future upgrades
  if (isHeatPump) {
    upgradeScore += 4;
    upgradeText = "**Excellent future positioning**: Already on the decarbonisation pathway. Future upgrades focus on optimisation (tariff switching, solar PV integration, battery storage) rather than wholesale system replacement. Preserves maximum flexibility for energy system evolution.";
  } else if (isSystem && hasStoredWater) {
    upgradeScore += 3;
    upgradeText = "**Very good future positioning**: Retaining stored hot water preserves compatibility with solar thermal, PV diversion, and future heat pump upgrades. Later transition to heat pump requires only boiler replacement, not pipework or cylinder changes, reducing long-term disruption and cost.";
  } else if (isRegular && hasStoredWater) {
    if (currentBoiler === "regular") {
      upgradeScore += 2;
      upgradeText = "**Good future positioning**: Like-for-like regular boiler replacement preserves open vented system, which can later upgrade to system boiler or heat pump with moderate work. Stored hot water maintained for future solar/heat pump integration. Lower flow temperatures already compatible with heat pumps.";
    } else {
      upgradeScore += 2;
      upgradeText = "**Good future positioning**: Regular boiler with stored water maintains upgrade flexibility. Can later convert to system boiler (sealed system) or heat pump. Stored hot water compatible with future solar thermal and PV diversion.";
    }
  } else if (isStorageCombi) {
    // ⚠️ Neutral / conditional
    upgradeScore += 0;
    upgradeText = "**Limited future positioning**: Storage combi removes cylinder, making later heat pump upgrade moderately disruptive. Would need to add separate cylinder (£1-2k extra) and modify pipework. However, integrated tank provides stepping stone if upgrading from standard combi. Consider system boiler instead for better long-term flexibility.";
  } else if (isCombi && !hasStoredWater) {
    // ❌ Poor upgrade positioning
    if (currentBoiler === "regular" || currentBoiler === "system") {
      upgradeScore -= 3;
      upgradeText = "**Poor future positioning**: Converting from stored hot water to combi removes the cylinder, closing off future upgrade paths. Reintroducing a cylinder later for heat pump compatibility typically costs more (£1.5-3k extra for cylinder + pipework modifications) and causes greater disruption than retaining one now. Removes solar thermal/PV diversion options.";
    } else if (currentBoiler === "combi") {
      upgradeScore += 0;
      upgradeText = "**Neutral future positioning**: Like-for-like combi replacement maintains status quo. Future heat pump conversion requires adding cylinder and extensive pipework changes (£2-4k extra vs system boiler baseline). Consider system boiler if planning long-term decarbonisation.";
    } else {
      upgradeScore -= 2;
      upgradeText = "**Poor future positioning**: Combi removes stored hot water, limiting future upgrade paths. Heat pump conversion later requires adding cylinder and pipework modifications (£2-4k extra). Blocks solar thermal and PV diversion integration. Consider system boiler for better long-term flexibility.";
    }
  }

  // Additional context for systems with stored water and good upgrade paths
  if ((isSystem || isRegular) && hasStoredWater && upgradeScore >= 2) {
    upgradeText += " This choice keeps doors open for staged decarbonisation over 10-20 years, reducing lifetime disruption and cost.";
  }

  return {
    upgradeScore,
    upgradeText
  };
}

/**
 * Shared constants for heat pump performance messaging
 */
const HEAT_PUMP_FLOW_TEMP_CAVEAT = "⚠ Performance depends on required flow temperature: With existing emitters (estimate), likely higher flow temps (55–70°C) → typically ~2–3 : 1 efficiency (low confidence). With low-temp emitters/UFH (upgraded), design 35–45°C → typically ~3.5–5 : 1 efficiency (higher confidence). Buffers/LLH don't create efficiency; flow temperature requirement does.";

const HYBRID_PATHWAY_NOTE = "ℹ If you want lower disruption now, a hybrid approach can be a stepping stone: upgrade emitters over time, then move toward full low-temp operation.";

/**
 * Evaluate a single system configuration option
 */
function evaluateOption({
  houseType,
  occupants,
  bathrooms,
  drawsPerDay,
  currentBoiler,
  currentWater,
  newBoiler,
  newWater,
  mainsPressure,
  flowRate,
  spaceAvailability = "medium",
  disruptionTolerance = "moderate",
  temperaturePreference = "comfort",
  solarPanels = "none",
  batteryStorage = "none",
  electricalCapacity = "unknown",
  costPriority = "balanced",
  budgetVsLongevity = "balanced",
  budgetVsGreen = "balanced",
  futureDemand = "stable",
  temporaryGuests = "none",
  plannedOccupancyChange,
  occupancyTiming = "unknown",
  waterHardness = "unknown",
  existingSystemAge,
  urgency = "planning",
  dhwDeliveryMethod = "unknown",
  heatEmitterType = "standard_rads",
  acceptsVisiblePipework = true,
  wallSpaceForOversizedRads = true,
  insulationLevel = "unknown",
  plannedInsulationUpgrades = false,
  fullReplacement = false,
  retrofitLevel = "medium",
  lowTempReadiness = "medium",
  emitterProfile,
  retrofitProfile
}: {
  houseType: string;
  occupants: number;
  bathrooms: number;
  drawsPerDay: number;
  currentBoiler: string;
  currentWater: string;
  newBoiler: string;
  newWater: string;
  mainsPressure: number;
  flowRate: number;
  spaceAvailability?: string;
  disruptionTolerance?: string;
  temperaturePreference?: string;
  solarPanels?: string;
  batteryStorage?: string;
  electricalCapacity?: string;
  costPriority?: string;
  budgetVsLongevity?: string;
  budgetVsGreen?: string;
  futureDemand?: string;
  temporaryGuests?: string;
  plannedOccupancyChange?: number;
  occupancyTiming?: string;
  waterHardness?: string;
  existingSystemAge?: number;
  urgency?: string;
  dhwDeliveryMethod?: 'gravity' | 'pumped' | 'unknown';
  heatEmitterType?: string;
  acceptsVisiblePipework?: boolean;
  wallSpaceForOversizedRads?: boolean;
  insulationLevel?: string;
  plannedInsulationUpgrades?: boolean;
  fullReplacement?: boolean;
  retrofitLevel?: RetrofitLevel;
  lowTempReadiness?: LowTempReadiness;
  emitterProfile: import('./types').EmitterProfile;
  retrofitProfile: import('./types').RetrofitProfile;
}): {
  score: number;
  dailyWasteL: number;
  dailyOverheadKWh: number;
  dailyCylinderLoss: number;
  pros: string[];
  cons: string[];
  relevant: Set<string>;
  futureUpgradePositioning: string;
} {
  const isCombi = newBoiler === "combi";
  const isStorageCombi = newBoiler === "storage_combi";
  const isASHP = newBoiler === "air_source_hp";
  const isGSHP = newBoiler === "ground_source_hp";
  const isHeatPump = isASHP || isGSHP;
  const isHybrid = newBoiler === "hybrid";

  // === SOLAR + BATTERY SYNERGY FLAGS ===
  const hasSolar = solarPanels === "present";
  const hasBattery = batteryStorage === "present";
  const hasSolarAndBattery = hasSolar && hasBattery;
  const planningSolar = solarPanels === "planned";
  const planningBattery = batteryStorage === "planned";

  // Helper function to check if water type matches a strategy (handles both old and new formats)
  const isWaterTypeStrategy = (water: any, strategy: string, variant?: string): boolean => {
    if (typeof water === 'object' && water !== null && 'strategy' in water) {
      const matches = water.strategy === strategy;
      if (variant) {
        return matches && water.variant === variant;
      }
      return matches;
    }
    // Handle legacy string format
    if (strategy === 'unvented') return water === 'unvented' || water === 'mixergy_unvented';
    if (strategy === 'open_vented') return water === 'open_vented' || water === 'mixergy_open';
    return water === strategy;
  };

  // Extract strategy and variant from water config (supports both old string and new object format)
  const waterObj = typeof newWater === 'object' && newWater !== null && 'strategy' in newWater ? newWater as { strategy: string; variant?: string } : null;
  const waterStrategy = waterObj ? waterObj.strategy : newWater;
  const waterVariant = waterObj ? (waterObj.variant || 'standard') : 'standard';
  
  const isCurrentOpen = currentWater === "open_vented" || currentWater === "mixergy_open" || currentWater === "gravity";
  const isUnvented = isWaterTypeStrategy(newWater, 'unvented');
  const isOpenVented = isWaterTypeStrategy(newWater, 'open_vented') || waterStrategy === "gravity";
  const isMixergy = waterVariant === "mixergy" || newWater === "mixergy_unvented" || newWater === "mixergy_open";
  const isHeatBattery = waterStrategy === "heat_battery";
  const isThermalStore = waterStrategy === "thermal_store";

  let score = 0;
  let dailyWasteL = 0;
  let dailyOverheadKWh = 0;
  let dailyCylinderLoss = 0;
  const pros: string[] = [];
  const cons: string[] = [];
  const relevant = new Set<string>();
  const normalizedDisruption = disruptionTolerance === "one_room_only" || disruptionTolerance === "minimal"
    ? "low"
    : disruptionTolerance === "two_three_rooms" || disruptionTolerance === "moderate"
      ? "medium"
      : "high";

  // Performance evaluation
  if (isHeatPump) {
    // Heat pump scoring - comprehensive evaluation based on evidence
    dailyWasteL = systemData.stored.wastePerDraw * drawsPerDay;

    if (isASHP) {
      const ashpData = systemData.heatPump.ashp;

      // Calculate realistic running costs with conditional SCOP
      const emitterSuitability = emitterProfile.lowTempSuitability;
      const emitterType = emitterProfile.type;
      const flowTemp = 55; // Default estimate for current emitters
      
      const gasCost = calculateRunningCostPerKWh("combi", false);
      const hpStandardCost = calculateRunningCostPerKWh("air_source_hp", false, emitterSuitability, emitterType, flowTemp);
      const hpTariffCost = calculateRunningCostPerKWh("air_source_hp", true, emitterSuitability, emitterType, flowTemp);
      const standardSavings = ((gasCost.costPerKWh - hpStandardCost.costPerKWh) / gasCost.costPerKWh) * 100;
      const tariffSavings = ((gasCost.costPerKWh - hpTariffCost.costPerKWh) / gasCost.costPerKWh) * 100;

      // Show SCOP range based on emitter configuration
      const effectiveScop = getEffectiveScop(emitterSuitability, emitterType, flowTemp);
      const lowTempConfirmed = isLowTempConfirmed(emitterSuitability, emitterType);
      
      if (lowTempConfirmed) {
        pros.push(`✓ SCOP 4.0–4.5 expected with low-temperature emitters (${emitterType === 'underfloor' ? 'underfloor heating' : 'suitable radiators'}). Design flow temperature 35–45°C enables optimal efficiency.`);
      } else {
        pros.push(`Expected SCOP: ${effectiveScop.toFixed(1)} with current emitters (estimated 50–55°C flow). With radiator upgrades: 4.0–4.5 (35–45°C flow).`);
      }
      // Be honest about running costs
      if (solarPanels === "present" || solarPanels === "planned") {
        pros.push(`✓ Running costs much lower than gas with solar PV offsetting 30-50% of electricity`);
      } else {
        cons.push(`Running cost reality: electricity is 4.7× gas price, so even with 350-500% efficiency, savings are ${standardSavings.toFixed(0)}% on standard tariff, ${tariffSavings.toFixed(0)}% with heat pump tariff`);
      }

      pros.push(`✓ £${ashpData.busGrant.toLocaleString()} BUS grant (net cost ~£${ashpData.netCapEx.toLocaleString()})`);
      pros.push(`✓ ${ashpData.lifespan} year lifespan (vs 12-15 for gas boilers)`);
      pros.push("✓ Zero carbon at point of use, future-proof as grid decarbonizes");
      score += 5;  // Base score for low-carbon heating

      cons.push(`Requires cylinder (140-300L) for hot water storage`);
      cons.push(`Annual maintenance ~£${ashpData.maintenanceCost} (specialist required)`);
      
      // Performance caveat: Two-scenario efficiency explanation
      cons.unshift(HEAT_PUMP_FLOW_TEMP_CAVEAT);
      relevant.add("cons:⚠ Performance varies significantly with flow temperature requirements");

      // Cylinder loss for heat pumps
      if (isHeatBattery) {
        dailyCylinderLoss = systemData.storage.heatBattery.standingLoss;
        pros.push("✓ Heat battery is 4× smaller than standard cylinder, 50-year lifespan");
        score += 2;
        relevant.add("pros:Heat battery is 4× smaller than standard cylinder");
      } else {
        dailyCylinderLoss = systemData.stored.cylLoss;
      }

      // Space requirements
      if (spaceAvailability === "small") {
        cons.push("⚠ ASHPs need outdoor space for external unit (typically 1m × 1m)");
        score -= 2;
        relevant.add("cons:⚠ ASHPs need outdoor space for external unit");
      } else {
        pros.push("✓ Adequate outdoor space for ASHP unit");
        score += 1;
      }

      // Disruption tolerance - ASHPs typically require radiator upgrades
      if (normalizedDisruption === "low") {
        cons.push("⚠ Typically requires radiator upgrades (£3k-£6k) and 28mm pipework for optimal performance");
        score -= 3;
        relevant.add("cons:⚠ Typically requires radiator upgrades and larger pipework");
      } else if (normalizedDisruption === "medium") {
        cons.push("Installation requires some disruption for radiator sizing and pipework upgrades");
        score -= 1;
      } else {
        pros.push("✓ You're prepared for the radiator and pipework upgrades needed for optimal performance");
        score += 2;
        relevant.add("pros:✓ Prepared for necessary upgrades");
      }

      // Low-temp readiness assessment (based on emitters + retrofit)
      // Note: This encompasses both emitter suitability and envelope quality
      if (lowTempReadiness === "low") {
        cons.push("⚠ Current emitters and/or insulation may need upgrades for best comfort and efficiency");
        cons.push("⚠ Emitter upgrade likely required: Standard radiators may require flow temperatures of 50-55°C, reducing SCOP. Oversized radiators or underfloor heating enable optimal 35-45°C operation.");
        score -= 4;
        relevant.add("cons:⚠ Emitter/insulation upgrades needed for optimal heat pump performance");
        // Add hybrid pathway note for low-temp readiness
        cons.push(HYBRID_PATHWAY_NOTE);
        relevant.add("cons:ℹ Hybrid pathway available as stepping stone");
      } else if (lowTempReadiness === "medium") {
        pros.push("✓ Home is reasonably suited for heat pump operation");
        cons.push("ℹ Flow temperature requirements: Property likely requires 45-50°C flow temperature. Emitter assessment recommended to optimize efficiency.");
        score += 1;
        // Add hybrid pathway note for medium-temp readiness
        cons.push(HYBRID_PATHWAY_NOTE);
        relevant.add("cons:ℹ Hybrid pathway available as stepping stone");
      } else if (lowTempReadiness === "high") {
        pros.push("✓ Excellent emitter and insulation profile - ideal for heat pump operating at optimal 35-45°C flow temperatures");
        score += 3;
        relevant.add("pros:✓ Ideal emitter and insulation for heat pump");
      }

      // Temperature preference - critical for heat pump efficiency
      if (temperaturePreference === "lower") {
        pros.push("✓ Your lower temperature preference (18-20°C) maximizes ASHP efficiency and running costs");
        score += 3;
        relevant.add("pros:✓ Lower temperature preference maximizes efficiency");
      } else if (temperaturePreference === "higher") {
        cons.push("⚠ Higher temperature preference (22-24°C) reduces ASHP efficiency and increases running costs");
        score -= 2;
        relevant.add("cons:⚠ Higher temperature preference reduces efficiency");
      } else {
        pros.push("✓ Comfort temperature (20-22°C) achievable with properly sized radiators");
        score += 1;
      }

      // Occupancy timing - impacts heat pump suitability
      if (occupancyTiming === "most_of_day") {
        pros.push("✓ Home occupied most of the day - ideal for heat pump's steady, continuous heating");
        score += 3;
        relevant.add("pros:✓ Daytime occupancy suits continuous heating");
      } else if (occupancyTiming === "evenings_weekends") {
        cons.push("⚠ Home empty during weekdays - heat pump's slow recovery from setback may be less comfortable than gas boiler");
        score -= 2;
        relevant.add("cons:⚠ Infrequent occupancy makes slow heat pump recovery less suitable");
      } else if (occupancyTiming === "mornings_evenings") {
        cons.push("⚠ Heat pumps work best with steady temperatures rather than rapid morning/evening warm-ups");
        score -= 1;
      }

      // Electrical capacity
      if (electricalCapacity === "insufficient") {
        cons.push("⚠ Electrical supply upgrade required (add £300-£750)");
        score -= 3;
        relevant.add("cons:⚠ Electrical supply upgrade required");
      } else if (electricalCapacity === "likely_ok") {
        pros.push("✓ Modern electrical supply likely adequate for ASHP");
        score += 1;
      }

    } else if (isGSHP) {
      const gshpData = systemData.heatPump.gshp;

      // Calculate realistic running costs with conditional SCOP
      const emitterSuitability = emitterProfile.lowTempSuitability;
      const emitterType = emitterProfile.type;
      const flowTemp = 55; // Default estimate for current emitters
      
      const gasCost = calculateRunningCostPerKWh("combi", false);
      const hpStandardCost = calculateRunningCostPerKWh("ground_source_hp", false, emitterSuitability, emitterType, flowTemp);
      const hpTariffCost = calculateRunningCostPerKWh("ground_source_hp", true, emitterSuitability, emitterType, flowTemp);
      const standardSavings = ((gasCost.costPerKWh - hpStandardCost.costPerKWh) / gasCost.costPerKWh) * 100;
      const tariffSavings = ((gasCost.costPerKWh - hpTariffCost.costPerKWh) / gasCost.costPerKWh) * 100;

      pros.push(`✓ COP ${gshpData.cop.toFixed(1)} stable year-round (most efficient)`);
      pros.push(`✓ ${gshpData.lifespan} year heat pump lifespan, ${gshpData.groundLoopLifespan} year ground loops`);
      pros.push(`✓ £${gshpData.busGrant.toLocaleString()} BUS grant (net cost ~£${gshpData.netCapEx.toLocaleString()} for trenches)`);

      // Be realistic about running costs
      if (solarPanels === "present" || solarPanels === "planned") {
        pros.push("✓ Lowest running costs of any heating system when combined with solar PV");
      } else {
        pros.push(`✓ Lowest running costs with heat pump tariff (saves ${tariffSavings.toFixed(0)}% vs gas), but only ${standardSavings.toFixed(0)}% savings on standard tariff due to electricity being 4.7× gas price`);
      }

      pros.push("✓ Zero carbon at point of use, most future-proof option");
      score += 7;  // Higher base score for best long-term solution

      cons.push(`Requires large outdoor space for horizontal trenches or boreholes`);
      cons.push(`High upfront cost (£${(gshpData.capExBeforeGrant / 1000).toFixed(0)}k-£${(gshpData.boreholeCapEx / 1000).toFixed(0)}k before grant)`);
      cons.push(`Annual maintenance ~£${gshpData.maintenanceCost} (specialist required)`);
      
      // Performance caveat: Two-scenario efficiency explanation (same as ASHP)
      cons.unshift(HEAT_PUMP_FLOW_TEMP_CAVEAT);
      relevant.add("cons:⚠ Performance varies significantly with flow temperature requirements");

      // Cylinder loss
      if (isHeatBattery) {
        dailyCylinderLoss = systemData.storage.heatBattery.standingLoss;
        pros.push("✓ Heat battery is 4× smaller than standard cylinder, 50-year lifespan");
        score += 2;
      } else {
        dailyCylinderLoss = systemData.stored.cylLoss;
      }

      // Space requirements - CRITICAL for GSHP
      if (spaceAvailability === "small") {
        cons.push("⚠ GSHP requires large outdoor area (150-250m² for trenches or space for boreholes) - NOT SUITABLE");
        score -= 8;  // Disqualifying factor
        relevant.add("cons:⚠ GSHP requires large outdoor area - NOT SUITABLE");
      } else if (spaceAvailability === "medium") {
        cons.push("⚠ GSHP typically needs large outdoor space - verify suitability with installer");
        score -= 3;
        relevant.add("cons:⚠ GSHP typically needs large outdoor space");
      } else {
        pros.push("✓ Large property with ample outdoor space ideal for GSHP ground loops");
        score += 3;
        relevant.add("pros:✓ Ample outdoor space ideal for GSHP");
      }

      // Disruption tolerance
      if (normalizedDisruption === "low") {
        cons.push("⚠ GSHP installation is highly disruptive (trenches/boreholes + radiator upgrades) - NOT RECOMMENDED");
        score -= 5;
        relevant.add("cons:⚠ GSHP installation highly disruptive");
      } else if (normalizedDisruption === "medium") {
        cons.push("Major disruption from ground work and radiator upgrades (weeks of work)");
        score -= 2;
      } else {
        pros.push("✓ You're prepared for the significant installation work (groundwork + radiator upgrades)");
        score += 3;
        relevant.add("pros:✓ Prepared for significant installation work");
      }
      
      // Low-temp readiness assessment (based on emitters + retrofit) - same as ASHP
      if (lowTempReadiness === "low") {
        cons.push("⚠ Current emitters and/or insulation may need upgrades for best comfort and efficiency");
        score -= 4;
        relevant.add("cons:⚠ Emitter/insulation upgrades needed for optimal heat pump performance");
        // Add hybrid pathway note for low-temp readiness
        cons.push(HYBRID_PATHWAY_NOTE);
        relevant.add("cons:ℹ Hybrid pathway available as stepping stone");
      } else if (lowTempReadiness === "medium") {
        pros.push("✓ Home is reasonably suited for heat pump operation");
        score += 1;
        // Add hybrid pathway note for medium-temp readiness
        cons.push(HYBRID_PATHWAY_NOTE);
        relevant.add("cons:ℹ Hybrid pathway available as stepping stone");
      } else if (lowTempReadiness === "high") {
        pros.push("✓ Excellent emitter and insulation profile - ideal for heat pump");
        score += 3;
        relevant.add("pros:✓ Ideal emitter and insulation for heat pump");
      }

      // Temperature preference
      if (temperaturePreference === "lower") {
        pros.push("✓ Your lower temperature preference (18-20°C) maximizes GSHP efficiency and minimizes running costs");
        score += 4;
        relevant.add("pros:✓ Lower temperature maximizes efficiency");
      } else if (temperaturePreference === "higher") {
        cons.push("Higher temperature preference (22-24°C) increases running costs, though still efficient");
        score -= 1;
      } else {
        pros.push("✓ Comfort temperature (20-22°C) easily achieved with GSHP");
        score += 2;
      }

      // Electrical capacity
      if (electricalCapacity === "insufficient") {
        cons.push("⚠ Electrical supply upgrade required (add £300-£750)");
        score -= 3;
        relevant.add("cons:⚠ Electrical supply upgrade required");
      } else if (electricalCapacity === "likely_ok") {
        pros.push("✓ Modern electrical supply likely adequate for GSHP");
        score += 1;
      }
    }

    // Heat emitter synergy (UFH vs radiators)
    if (heatEmitterType === "underfloor_heating") {
      pros.push("✓ Underfloor heating is ideal for low-temperature heat pumps (excellent SCOP)");
      score += 10;
      relevant.add("pros:✓ Underfloor heating maximizes heat pump efficiency");
    } else if (heatEmitterType === "standard_rads") {
      cons.push("Standard radiators usually need upgrading for 35-45°C flow temperatures");
      score -= 5;
      relevant.add("cons:Standard radiators need upgrade for heat pump flow temperatures");
    } else if (heatEmitterType === "oversized_rads") {
      pros.push("✓ Oversized radiators already suited to lower flow temperatures");
      score += 3;
      relevant.add("pros:✓ Oversized radiators suit heat pump flow temperatures");
    }

    // Visible pipework preference
    if (!acceptsVisiblePipework) {
      cons.push("Visible surface-mounted pipework may be required for heat pump retrofit");
      score -= 4;
      relevant.add("cons:Visible surface pipework may be required");
    }

    // Wall space for oversized radiators
    if (!wallSpaceForOversizedRads && heatEmitterType !== "underfloor_heating") {
      cons.push("Limited wall space makes oversized radiators difficult for heat pump output");
      score -= 6;
      relevant.add("cons:Limited wall space for oversized radiators");
    }

    // Insulation and air tightness
    // IMPORTANT: Only apply insulationLevel penalty if retrofit data is NOT provided
    // If retrofit data is available, lowTempReadiness already accounts for insulation
    const hasRetrofitData = (retrofitProfile.loftInsulation !== 'unknown' ||
                            retrofitProfile.wallInsulation !== 'unknown' ||
                            retrofitProfile.windows !== 'unknown' ||
                            retrofitProfile.draughtProofing !== 'unknown');
    
    if (!hasRetrofitData && insulationLevel === "poor") {
      cons.push("Low insulation/airtightness reduces heat pump efficiency and increases running costs");
      score -= plannedInsulationUpgrades ? 4 : 10;
      relevant.add("cons:Low insulation reduces heat pump efficiency");
      if (plannedInsulationUpgrades) {
        pros.push("✓ Planned insulation upgrades will improve heat pump performance");
        score += 2;
      }
    } else if (!hasRetrofitData && insulationLevel === "high") {
      pros.push("✓ Good insulation/airtightness lets heat pump run efficiently at low flow temperatures");
      score += 3;
      relevant.add("pros:✓ Good insulation supports heat pump efficiency");
    }

    // === SOLAR PV + BATTERY SYNERGY - MAJOR SCORE MULTIPLIER ===
    // When both solar PV and battery storage are present, heat pump economics shift dramatically
    // Heat pumps turn 1 kWh of "free" solar electricity into 3-4 kWh of heat (CoP 3-4)
    if (hasSolarAndBattery) {
      // MASSIVE SCORE BUMP: Solar + Battery + Heat Pump is the gold standard
      pros.push("✓ OPTIMAL SETUP: Solar PV + Battery + Heat Pump = near-zero running costs");
      pros.push("✓ Battery increases solar self-consumption from ~30% to ~75%+ - most heat pump electricity is effectively free");
      pros.push("✓ Heat pump CoP of 3-4 means 1 kWh of free solar = 3-4 kWh of heat - outstanding efficiency");
      pros.push("✓ Summer: Operational costs drop to near-zero when solar generation exceeds demand");
      pros.push("✓ Winter: Battery can time-shift cheap off-peak electricity to heat pump operation");
      score += 25; // Major score bump for optimal electric heating setup
      relevant.add("pros:✓ Solar + Battery + Heat Pump = optimal efficiency");
    } else if (hasSolar && planningBattery) {
      // Solar present, battery planned
      pros.push("✓ Existing solar PV already offsets 30-50% of heat pump electricity");
      pros.push("✓ Planned battery will increase self-consumption to ~75%+ - heat pump running costs will drop significantly");
      score += 12;
      relevant.add("pros:✓ Solar + planned battery enhances heat pump economics");
    } else if (hasSolar) {
      // Solar only (no battery)
      pros.push("✓ Existing solar PV can offset 30-50% of heat pump electricity, reducing running costs significantly");
      pros.push("Note: Adding battery storage would increase self-consumption to ~75%+ for maximum savings");
      score += 4;
      relevant.add("pros:✓ Solar PV offsets heat pump electricity");
    } else if (hasBattery && planningSolar) {
      // Battery present, solar planned
      pros.push("✓ Existing battery ready to store planned solar generation for heat pump use");
      pros.push("✓ Battery currently enables off-peak charging - planned solar will add daytime free energy");
      score += 8;
      relevant.add("pros:✓ Battery + planned solar ready for heat pump");
    } else if (planningSolar || planningBattery) {
      // Planning either solar or battery
      pros.push("✓ Planned solar PV/battery will work excellently with heat pump for energy independence");
      score += 3;
      relevant.add("pros:✓ Planned solar PV pairs well with heat pump");
    } else {
      cons.push("Consider adding solar PV and battery storage for maximum running cost savings with heat pump - can reduce operating costs by 60-70%");
    }

  } else if (isStorageCombi) {
    // Storage combi boiler (e.g., Viessmann Vitodens 111-W)
    const wastePerDraw = systemData.storageCombi.baseWaste[houseType as keyof typeof systemData.storageCombi.baseWaste] ?? 3.0;
    const wait = systemData.storageCombi.baseWait[houseType as keyof typeof systemData.storageCombi.baseWait] ?? 25;
    dailyWasteL = wastePerDraw * drawsPerDay;
    dailyOverheadKWh = (systemData.storageCombi.startupKWh + systemData.storageCombi.postPurgeKWh) * drawsPerDay;

    pros.push("Single appliance with integrated 46L storage tank");
    pros.push(`Sustained ${systemData.storageCombi.flowRate} L/min flow rate (better than standard combi)`);
    pros.push("94-98% efficiency (A-rated condensing technology)");
    cons.push("⚠ Washing-machine sized (720-900mm tall × 440-600mm wide × 480-600mm deep) - larger than wall-hung standard combi");
    cons.push(`Hot water lag ${wait}s to tap (better than standard combi)`);
    cons.push(`Wastes about ${wastePerDraw.toFixed(1)} L per draw (${dailyWasteL.toFixed(1)} L/day)`);

    // Reliability and maintenance considerations
    cons.push("⚠ Increased mechanical complexity: integrated tank, cylinder loading pump, and additional sensors create more failure points than standard combis");
    cons.push("⚠ Shorter average lifespan (10-15 years) vs regular boilers (15-25 years) due to harder-working components and frequent start-ups");
    cons.push("⚠ Vulnerable to limescale in hard water areas - can block heat exchangers every 6 months despite scale reducers");
    cons.push("⚠ Heat exchanger scaling causes 'kettling' and can lead to cracking, with repair costs approaching new boiler price");
    cons.push("⚠ Specialist maintenance required (£150-£300 vs £80-£120 for standard boiler) - many engineers lack storage combi training");
    cons.push("⚠ Fixed repair costs higher: manufacturer-backed service ~£159/year, premium repairs £400+");
    cons.push("⚠ Higher lifetime costs: Purchase (£1,500-2,500) + more frequent repairs + shorter economic life = 15-25% more than regular combi or system boiler over 10 years");
    cons.push("⚠ Hot water priority operation: central heating disabled while tank recharges");
    cons.push("⚠ Potential 'hot water dump' effect: residual tank water may cause radiator overheating after hot water draw");
    cons.push("⚠ No electric immersion backup: if gas boiler fails, household has no hot water (unlike system boiler + cylinder)");

    // Apply comprehensive penalty factors based on real-world data
    const penalties = systemData.storageCombi.penalties;
    // Apply all penalty factors (total: -80 points)
    Object.values(penalties).forEach(penalty => {
      score += penalty;
    });
    relevant.add("cons:⚠ Higher complexity, repair costs, and lifetime costs than alternatives");

    // Suitability based on household size - only minimal bonus in edge cases
    if (occupants <= 4 && bathrooms <= 2) {
      // Reduced from +4 to -2: storage combi is rarely the best choice even for small households
      score -= 2;
      relevant.add("cons:Consider regular combi or system boiler for better value");
    } else {
      cons.push("For larger households, consider system boiler with larger cylinder");
      score -= 5;  // Stronger penalty for unsuitable household size
    }

    // Size penalty: storage combis are NOT space-efficient compared to wall-hung combis
    if (spaceAvailability === "small") {
      cons.push("⚠ Storage combi is washing-machine sized - larger than wall-hung regular combi");
      score -= 5;  // Penalty instead of bonus: wall-hung combi is more space-efficient
      relevant.add("cons:⚠ Larger footprint than wall-hung alternatives");
    } else {
      score -= 3;  // Still penalize, but less severely
    }

  } else if (isCombi) {
    // Standard combi boiler
    const wastePerDraw = systemData.combi.baseWaste[houseType as keyof typeof systemData.combi.baseWaste] ?? 5.5;
    const wait = systemData.combi.baseWait[houseType as keyof typeof systemData.combi.baseWait] ?? 45;
    dailyWasteL = wastePerDraw * drawsPerDay;
    dailyOverheadKWh = (systemData.combi.startupKWh + systemData.combi.postPurgeKWh) * drawsPerDay;
    pros.push("Single appliance, frees space");
    cons.push(`Hot water lag ${wait}s to tap`);
    cons.push(`Wastes about ${wastePerDraw.toFixed(1)} L per draw (${dailyWasteL.toFixed(1)} L/day)`);
    cons.push(`Per-draw energy overhead ≈ ${dailyOverheadKWh.toFixed(2)} kWh/day`);

    // Suitability for combi based on household size
    if (occupants <= 3 && bathrooms <= 2) {
      score += 3;
      relevant.add("pros:Single appliance, frees space");
    } else {
      score -= 2;
      relevant.add(`cons:Hot water lag ${wait}s to tap`);
    }

    // Occupancy timing - combis excel with bursty heating patterns
    if (occupancyTiming === "evenings_weekends" || occupancyTiming === "mornings_evenings") {
      pros.push("✓ Quick warm-up ideal for short heating periods in mornings and evenings");
      score += 2;
      relevant.add("pros:✓ Fast response suits irregular occupancy");
    } else if (occupancyTiming === "most_of_day") {
      cons.push("⚠ More frequent cycling with all-day heating may reduce efficiency vs system boiler with cylinder");
      score -= 1;
    }

  } else {
    // Stored water system (cylinder) - system/regular boilers
    dailyWasteL = systemData.stored.wastePerDraw * drawsPerDay;

    if (isHeatBattery) {
      dailyCylinderLoss = systemData.storage.heatBattery.standingLoss;
      pros.push("Heat battery: 4× smaller than standard cylinder, 50-year lifespan");
      pros.push(`Significantly lower standing loss due to superior insulation (${dailyCylinderLoss.toFixed(1)} kWh/day vs ${systemData.stored.cylLoss.toFixed(1)} kWh/day for typical cylinder - performance depends on use pattern)`);
      score += 3;
      relevant.add("pros:Heat battery provides compact, long-lasting storage");

      // Solar PV synergy for heat battery
      if (solarPanels === "present") {
        pros.push("✓ Solar PV can charge heat battery during off-peak times for maximum efficiency");
        score += 3;
        relevant.add("pros:✓ Solar PV charging synergy");
      } else if (solarPanels === "planned") {
        pros.push("✓ Planned solar PV will pair well with heat battery for energy storage");
        score += 2;
      }
    } else if (isThermalStore) {
      dailyCylinderLoss = systemData.storage.thermalStore.standingLoss;
      pros.push("Thermal store enables multiple heat sources (boiler, solar, wood stove)");
      pros.push("No legionella risk (stores primary water, not potable)");
      cons.push(`Higher standing loss (${dailyCylinderLoss.toFixed(1)} kWh/day)`);

      if (solarPanels === "present") {
        pros.push("✓ Solar Ready: Excellent for integrating with solar thermal or PV diverters (myenergi eddi, Solar iBoost)");
        score += 3;
        relevant.add("pros:✓ Excellent for solar integration");
      } else if (solarPanels === "planned") {
        pros.push("✓ Solar Ready: Excellent for future integration with solar thermal or PV diverters (myenergi eddi, Solar iBoost)");
        score += 3;
        relevant.add("pros:✓ Excellent for solar integration");
      } else {
        pros.push("✓ Solar Ready: Can integrate with solar thermal or PV diverters (myenergi eddi, Solar iBoost)");
      }
      score += 2;
    } else if (isMixergy) {
      // Mixergy smart cylinders - professionally preferred pairing with system boilers
      // IMPORTANT: Heat pumps need to heat the full cylinder volume to maintain efficiency,
      // so Smart Stratification is disabled in heat pump mode
      const mixergyWithHeatPump = isHeatPump;

      if (mixergyWithHeatPump) {
        // Heat pump mode: stratification disabled, but still 5-10% efficiency boost over standard
        dailyCylinderLoss = systemData.stored.cylLoss * 0.9; // Only modest improvement without stratification

        // Heat pump + Mixergy specific benefits
        pros.push("✓ Mixergy is Heat Pump Ready - provides 5-10% efficiency boost over standard cylinders");
        pros.push("✓ Designed with ports for external plate heat exchangers - optimal for ASHP pairing");
        pros.push("✓ Mixergy app control allows heating during off-peak times or when electricity is cheapest");
        pros.push("✓ Electric immersion backup ensures hot water even if heat pump is under maintenance");
        
        // Add conditional pressure/flow warnings
        if (mainsPressure < 1.5) {
          cons.push(`⚠ Caution: Static pressure (${mainsPressure} bar) is below recommended minimum (1.5 bar) for optimal unvented performance`);
        }
        
        pros.push("✓ Consistent mains pressure across multiple outlets simultaneously");

        // Transparent about stratification limitation
        cons.push("⚠ Note: 'Smart Stratification' is disabled in heat pump mode - heat pumps need to heat the full tank volume to maintain CoP efficiency. The 10-20% gas reduction benefit applies to boiler configurations only.");
        cons.push(`Cylinder standing loss ≈ ${dailyCylinderLoss.toFixed(1)} kWh/day (modest improvement in heat pump mode)`);
      } else {
        // Standard boiler mode: full stratification benefits
        dailyCylinderLoss = systemData.stored.cylLoss * 0.7; // 30% lower standing loss due to smart stratification

        // Core Mixergy benefits (boiler mode)
        pros.push("✓ Smart stratified heating can reduce gas consumption by 10-20% depending on draw pattern and control strategy, and delivers hot water 10× faster than standard cylinders");
        pros.push("✓ Top-down volumetric heating - heat only what you need, matching combi efficiency without combi drawbacks");
        pros.push("✓ Mixergy app control allows heating during solar generation or off-peak times");
        pros.push("✓ Longer boiler burns → better modulation and boiler efficiency");

        // Reliability advantages over storage combis
        pros.push("✓ System boiler + Mixergy pairing avoids storage combi vulnerabilities: simpler mechanics, typically 15-25 year lifespan depending on quality and maintenance (vs 10-15 for storage combis)");
        pros.push("✓ No internal plate heat exchanger scaling issues - external heat exchangers are practical to clean/replace without dismantling");
        pros.push("✓ Electric immersion backup ensures hot water even if boiler fails (unlike storage combis)");
        pros.push("✓ Consistent mains pressure across multiple outlets simultaneously - no flow rate drops during simultaneous use");

        // Future-proofing and integration
        pros.push("✓ Heat pump ready: designed with ports for external plate heat exchangers - future ASHP upgrade without replacing cylinder");
        cons.push("Note: If upgrading to a heat pump in future, 'Smart Stratification' will be disabled (heat pumps need full tank heating for efficiency). The 5-10% efficiency boost over standard cylinders still applies.");

        cons.push(`Cylinder standing loss ≈ ${dailyCylinderLoss.toFixed(1)} kWh/day (30% better than standard due to smart stratification)`);
      }

      // Common benefits regardless of heat source
      pros.push("✓ Works at all water pressures (though 22mm feed + 30L/min recommended for optimal unvented performance)");
      score += 6; // Increased score for comprehensive advantages
      relevant.add("pros:✓ System boiler + Mixergy = superior reliability and future-proofing vs storage combis");

      // === MIXERGY + SOLAR + BATTERY SYNERGY ===
      // Mixergy is uniquely suited - can "trickle charge" from as little as 100W of excess solar
      // Unlike standard cylinders that need ~3kW to fire the immersion, Mixergy modulates 30W-3000W
      // When battery is present, Mixergy acts as a "Thermal Battery" for overflow energy
      if (hasSolarAndBattery) {
        // TOP TIER: Solar + Battery + Mixergy = maximum self-consumption
        pros.push("✓ TOP TIER SETUP: Solar PV + Battery + Mixergy = free hot water system");
        pros.push("✓ THERMAL BATTERY: When house battery is full, Mixergy soaks up remaining excess PV as stored hot water - prevents low-value export");
        pros.push("✓ PEAK SHIFTING: Battery can discharge to Mixergy during off-peak windows if solar was insufficient during the day");
        pros.push("✓ Grid Balancing: Smart integration means hot water is effectively free - costs drop to near-zero");
        pros.push("✓ Mixergy modulates 30W-3000W to match exactly what solar panels produce - standard cylinders need ~3kW");
        pros.push("✓ Mixergy Solar X built-in diverter or compatible with myenergi eddi, Solar iBoost");
        score += 15; // Major score bump for optimal hot water setup
        relevant.add("pros:✓ Solar + Battery + Mixergy = optimal free hot water setup");
      } else if (hasSolar && planningBattery) {
        // Solar present, battery planned - excellent future setup
        pros.push("✓ Mixergy Solar X already provides free hot water from excess solar PV");
        pros.push("✓ Planned battery will enable Mixergy as 'Thermal Battery' overflow - maximum self-consumption");
        pros.push("✓ Battery will enable peak-shifting: charge Mixergy from battery during off-peak when solar insufficient");
        pros.push("✓ Mixergy modulates 30W-3000W to match solar output exactly");
        score += 10;
        relevant.add("pros:✓ Solar + planned battery optimizes Mixergy hot water");
      } else if (hasSolar) {
        // Solar only - still great
        pros.push("✓ Mixergy Solar X includes built-in diverter - prioritizes free solar energy as 'hot water battery' without additional hardware");
        pros.push("✓ Self-consumption optimization stores excess solar PV as hot water, maximizing energy independence");
        pros.push("✓ Solar Ready: Also compatible with third-party diverters (myenergi eddi, Solar iBoost) if preferred");
        pros.push("✓ Key advantage: Mixergy modulates power 30W-3000W to match exactly what solar panels produce - standard cylinders need ~3kW to fire the immersion");
        pros.push("Tip: Adding battery storage would enable Mixergy as 'Thermal Battery' overflow for maximum savings");
        score += 5;
        relevant.add("pros:✓ Mixergy Solar X = seamless solar PV integration without extra hardware");
      } else if (hasBattery && planningSolar) {
        // Battery present, solar planned
        pros.push("✓ Existing battery ready for planned solar - Mixergy will act as 'Thermal Battery' for overflow");
        pros.push("✓ Battery can already shift off-peak electricity to Mixergy heating");
        pros.push("✓ Planned solar PV will enable near-free hot water with Mixergy");
        score += 6;
        relevant.add("pros:✓ Battery + planned solar ready for Mixergy integration");
      } else if (hasBattery) {
        // Battery only (no solar yet)
        pros.push("✓ Existing battery enables peak-shifting: charge Mixergy from battery during off-peak electricity rates");
        pros.push("✓ Adding solar PV would enable Mixergy as 'Thermal Battery' for overflow energy - maximum savings");
        score += 3;
        relevant.add("pros:✓ Battery enables Mixergy peak-shifting");
      } else if (planningSolar || planningBattery) {
        pros.push("✓ Mixergy Solar X model ready for future solar PV with built-in diverter and smart scheduling");
        pros.push("✓ Solar Ready: Also compatible with third-party diverters (myenergi eddi, Solar iBoost)");
        pros.push("✓ Can 'trickle charge' from as little as 100W excess solar - unique advantage over standard cylinders");
        score += 4;
        relevant.add("pros:✓ Future-ready for solar PV with integrated diverter");
      } else {
        // Even without solar, flag as Solar Ready for future upgrade
        pros.push("✓ Solar Ready: Compatible with Mixergy Solar X diverter, myenergi eddi, or Solar iBoost for future solar PV");
      }
    } else {
      // Standard cylinder
      dailyCylinderLoss = systemData.stored.cylLoss;
      pros.push("Shorter tap-to-hot, better comfort");
      pros.push("Longer boiler burns → better modulation");
      cons.push(`Cylinder standing loss ≈ ${dailyCylinderLoss.toFixed(1)} kWh/day`);
      score += 3;

      // Solar PV + Battery synergy for standard cylinders with immersion/diverter
      if (hasSolarAndBattery) {
        pros.push("✓ Solar + Battery: Can use PV diverters (myenergi eddi, Solar iBoost) to heat water with excess solar");
        pros.push("✓ Battery can provide backup heating during low-solar periods via immersion");
        cons.push("Note: Standard cylinders need ~3kW to fire immersion - less efficient than modulating systems like Mixergy (30W-3000W)");
        score += 4;
        relevant.add("pros:✓ Solar + Battery for hot water heating");
      } else if (hasSolar) {
        pros.push("✓ Solar Ready: Can use PV diverters (myenergi eddi, Solar iBoost) to heat water with excess solar, reducing gas use");
        pros.push("Note: Standard cylinders typically need ~3kW to fire the immersion - 'all or nothing' compared to modulating systems");
        score += 2;
        relevant.add("pros:✓ Solar PV diverter opportunity");
      } else if (planningSolar || planningBattery) {
        pros.push("✓ Solar Ready: Future option to add PV diverter (myenergi eddi, Solar iBoost) for hot water heating");
        score += 1;
      } else {
        // Flag as Solar Ready for future upgrade
        pros.push("✓ Solar Ready: Compatible with PV diverters (myenergi eddi, Solar iBoost) for future solar integration");
      }
    }

    if (occupants >= 4 || bathrooms >= 2) {
      pros.push("Better for multiple/simultaneous draws");
      score += 2;
      relevant.add("pros:Better for multiple/simultaneous draws");
    }

    // Occupancy timing - system boilers with stored DHW suit both patterns well
    if (occupancyTiming === "most_of_day") {
      pros.push("✓ Stored hot water with low standing loss ideal for daytime occupancy");
      score += 2;
      relevant.add("pros:✓ Continuous hot water availability suits daytime use");
    } else if (occupancyTiming === "evenings_weekends" || occupancyTiming === "mornings_evenings") {
      pros.push("✓ Can heat cylinder during off-peak times for use when needed");
      score += 1;
    }
  }

  // Gas boiler thermostat compatibility - applies to all gas systems (combi, system, regular)
  const isGasBoiler = isCombi || isStorageCombi || newBoiler === "system" || newBoiler === "regular";
  if (isGasBoiler) {
    // Modern boilers support load compensation
    // This enables precise temperature control and 10-15% additional fuel savings
    pros.push("✓ Modern gas boilers support load compensation for smart thermostat efficiency");

    // Add specific thermostat recommendation based on boiler type
    if (newBoiler === "system" || newBoiler === "regular") {
      // System/Regular boilers benefit most from load compensation
      pros.push("✓ System/Regular boilers ideal for smart load compensation - longer modulated burns for maximum efficiency");
      cons.push("IMPORTANT: Use a load-compensating thermostat to achieve full efficiency. Basic on/off smart stats lose ~50% of the efficiency benefit.");
      score += 2; // Bonus for systems that pair well with smart thermostats
      relevant.add("pros:✓ Smart thermostat compatibility for load compensation");
    } else if (isCombi) {
      // Combis still benefit but have more start/stop cycles
      pros.push("✓ Load-compensating thermostat reduces wasteful on/off cycling");
      cons.push("Note: Combi boilers have more start/stop cycles than system boilers - smart thermostat helps but can't eliminate this");
    }

    // General thermostat education
    if (!isMixergy) {
      // Mixergy already has app control mentioned
      cons.push("Bimetallic strip thermostats (±1.5-3°C swing) waste 10-15% fuel - upgrade to smart load compensation for precision");
    }
  }

  // Water pressure evaluation
  if (isHeatPump) {
    // Heat pumps are sealed systems - less pressure-sensitive than combis
    const reqs = systemData.pressureRequirements.heatPump;
    if (mainsPressure < reqs.minPressure || flowRate < reqs.minFlowRate) {
      cons.push(`Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – marginal but likely workable for heat pump sealed system`);
      score -= 1;
    } else {
      pros.push(`✓ Water pressure (${mainsPressure} bar / ${flowRate} L/min) adequate for heat pump system`);
      score += 1;
    }
  } else if (isStorageCombi) {
    const reqs = systemData.pressureRequirements.storageCombi;
    if (mainsPressure < reqs.minPressure || flowRate < reqs.minFlowRate) {
      cons.push(`⚠ Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – below storage combi minimum (${reqs.minPressure} bar / ${reqs.minFlowRate} L/min)`);
      score -= 4;
      relevant.add(`cons:⚠ Water pressure below storage combi minimum`);
    } else if (mainsPressure < reqs.goodPressure || flowRate < reqs.goodFlowRate) {
      cons.push(`Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – adequate for storage combi (best: >${reqs.goodPressure} bar / ${reqs.goodFlowRate} L/min)`);
      score -= 1;
    } else {
      pros.push(`✓ Water pressure test confirms good supply for storage combi (${mainsPressure} bar / ${flowRate} L/min)`);
      score += 2;
      relevant.add(`pros:✓ Good water pressure for storage combi`);
    }
  } else if (isCombi) {
    const reqs = systemData.pressureRequirements.combi;
    if (mainsPressure < reqs.minPressure || flowRate < reqs.minFlowRate) {
      cons.push(`⚠ Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – below combi minimum (${reqs.minPressure} bar / ${reqs.minFlowRate} L/min)`);
      score -= 5;
      relevant.add(`cons:⚠ Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – below combi minimum (${reqs.minPressure} bar / ${reqs.minFlowRate} L/min)`);
    } else if (mainsPressure < reqs.goodPressure || flowRate < reqs.goodFlowRate) {
      cons.push(`Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – marginal for combi (best: >${reqs.goodPressure} bar / ${reqs.goodFlowRate} L/min)`);
      score -= 2;
      relevant.add(`cons:Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – marginal for combi (best: >${reqs.goodPressure} bar / ${reqs.goodFlowRate} L/min)`);
    } else {
      pros.push(`✓ Water pressure test confirms good supply for combi (${mainsPressure} bar / ${flowRate} L/min)`);
      score += 2;
      relevant.add(`pros:✓ Water pressure test confirms good supply for combi (${mainsPressure} bar / ${flowRate} L/min)`);
    }
  } else if (isUnvented) {
    // Check if it's a Mixergy unvented system
    if (isMixergy) {
      const reqs = systemData.pressureRequirements.mixergyUnvented;
      // Weighted risk approach: Mixergy works at lower pressures than standard unvented
      // but performance varies based on pressure tier
      if (flowRate >= reqs.goodFlowRate && mainsPressure >= reqs.goodPressure) {
        // Optimal: Full performance
        pros.push(`✓ Excellent water supply (${mainsPressure} bar / ${flowRate} L/min) - optimal for Mixergy with 22mm feed`);
        score += 3;
        relevant.add(`pros:✓ Optimal water supply for Mixergy unvented`);
      } else if (mainsPressure >= reqs.goodPressure) {
        // Good pressure, adequate flow
        pros.push(`✓ Good water pressure (${mainsPressure} bar / ${flowRate} L/min) for Mixergy - works at any pressure`);
        score += 2;
        relevant.add(`pros:✓ Good pressure for Mixergy`);
      } else if (mainsPressure >= reqs.safePressure) {
        // Warning tier: Operational but performance may be reduced at high-draw periods
        pros.push(`✓ Mixergy operates at ${mainsPressure} bar / ${flowRate} L/min - system will work, but performance may be reduced during high-draw periods (e.g., multiple outlets)`);
        cons.push(`⚠ Water pressure (${mainsPressure} bar) is below optimal ${reqs.goodPressure} bar - consider this for peak demand scenarios`);
        score += 1;
        relevant.add(`pros:✓ Mixergy works at moderate pressures with advisory`);
      } else if (mainsPressure >= reqs.minPressure) {
        // Below safe threshold but above minimum: Works but with clear performance trade-offs
        cons.push(`⚠ Water pressure ${mainsPressure} bar is below the safe operational threshold (${reqs.safePressure} bar). Mixergy will still function, but expect reduced performance at high-draw periods. Consider a pressure booster if issues arise.`);
        score -= 1;
        relevant.add(`cons:⚠ Mixergy marginal pressure warning`);
      } else {
        // Below absolute minimum: Still works but strongly advised
        cons.push(`⚠ Water pressure ${mainsPressure} bar is below operational minimum (${reqs.minPressure} bar). While Mixergy is forgiving, you may experience issues. A pressure booster pump is recommended.`);
        score -= 2;
        relevant.add(`cons:⚠ Mixergy very low pressure warning`);
      }
    } else {
      // Standard unvented cylinder
      const reqs = systemData.pressureRequirements.unvented;
      if (mainsPressure < reqs.minPressure || flowRate < reqs.minFlowRate) {
        cons.push(`⚠ Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – below unvented minimum (${reqs.minPressure} bar / ${reqs.minFlowRate} L/min)`);
        score -= 4;
        relevant.add(`cons:⚠ Water pressure below unvented minimum`);
      } else if (mainsPressure < reqs.goodPressure || flowRate < reqs.goodFlowRate) {
        cons.push(`Water pressure test shows ${mainsPressure} bar / ${flowRate} L/min – adequate for unvented (best: >${reqs.goodPressure} bar / ${reqs.goodFlowRate} L/min)`);
        score -= 1;
        relevant.add(`cons:Water pressure adequate but not optimal for unvented`);
      } else {
        pros.push(`✓ Water pressure test confirms good supply for unvented (${mainsPressure} bar / ${flowRate} L/min)`);
        score += 2;
        relevant.add(`pros:✓ Good water pressure for unvented`);
      }
    }
  } else if (isOpenVented) {
    // Check if it's a Mixergy open vented system
    if (isMixergy) {
      const reqs = systemData.pressureRequirements.mixergyOpen;
      // Weighted risk approach for Mixergy open vented
      if (mainsPressure >= reqs.goodPressure && flowRate >= reqs.goodFlowRate) {
        pros.push(`✓ Mixergy open vented works excellently with your water pressure (${mainsPressure} bar / ${flowRate} L/min)`);
        pros.push(`✓ Your good supply allows option to upgrade to Mixergy unvented in future`);
        score += 3;
        relevant.add(`pros:✓ Mixergy open vented optimal with upgrade path`);
      } else if (mainsPressure >= reqs.safePressure) {
        pros.push(`✓ Mixergy open vented works well with your water pressure (${mainsPressure} bar / ${flowRate} L/min)`);
        score += 2;
        relevant.add(`pros:✓ Mixergy open vented works at all pressures`);
      } else if (mainsPressure >= reqs.minPressure) {
        // Warning tier for open vented
        pros.push(`✓ Mixergy open vented operates at ${mainsPressure} bar / ${flowRate} L/min - system will work`);
        cons.push(`⚠ Water pressure (${mainsPressure} bar) is below optimal - performance may be reduced during high-draw periods`);
        score += 1;
        relevant.add(`pros:✓ Mixergy open vented works with advisory`);
      } else {
        // Very low pressure - still works but with warning
        cons.push(`⚠ Water pressure ${mainsPressure} bar is very low. Mixergy open vented is forgiving but you may experience reduced performance.`);
        score += 0;
        relevant.add(`cons:⚠ Mixergy open vented low pressure warning`);
      }
    } else {
      // Standard open vented cylinder
      const reqs = systemData.pressureRequirements.openVented;
      if (mainsPressure < reqs.minPressure || flowRate < reqs.minFlowRate) {
        pros.push(`✓ Open vented system works with your low pressure (${mainsPressure} bar / ${flowRate} L/min)`);
        score += 3;
        relevant.add(`pros:✓ Open vented works with low pressure`);
      } else if (mainsPressure >= reqs.goodPressure && flowRate >= reqs.goodFlowRate) {
        cons.push(`Your good water pressure (${mainsPressure} bar / ${flowRate} L/min) is under-utilized by vented cylinder. Consider upgrading to unvented cylinder for better hot water performance.`);
        score -= 1;
        relevant.add(`cons:Good pressure under-utilized by vented cylinder`);
      } else {
        pros.push(`✓ Open vented system works with your pressure (${mainsPressure} bar / ${flowRate} L/min)`);
        score += 1;
        relevant.add(`pros:✓ Open vented works with your pressure`);
      }
    }
  }

  // === NEW: UNVENTED VS OPEN-VENTED PRIORITY LOGIC ===
  // Fix 2: Increase unvented score for higher occupancy and existing unvented systems
  
  // Performance bonus: If occupancy >= 3, unvented systems perform better
  // Add conditional wording based on flow rate
  if (isUnvented && occupants >= 3) {
    if (flowRate < 15) {
      pros.push(`✓ Unvented cylinder provides good performance for ${occupants} occupants, though flow rate (${flowRate} L/min) may limit simultaneous use performance`);
    } else {
      pros.push(`✓ Unvented cylinder provides excellent performance for ${occupants} occupants - mains pressure at all taps simultaneously`);
    }
    score += 20;
    relevant.add(`pros:✓ Unvented performance suits ${occupants} occupants`);
  }
  
  // Existing infrastructure bonus: If current system is already unvented, keep it
  if (isUnvented && (currentWater === "unvented" || currentWater === "mixergy_unvented")) {
    pros.push(`✓ Existing Infrastructure: Keeping unvented system avoids conversion costs and maintains proven performance`);
    score += 30;
    relevant.add(`pros:✓ Existing unvented infrastructure preserved`);
  }
  
  // Cap "Avoids Disruption" advantage for open_vented when it conflicts with performance needs
  // If occupants >= 3 and current system is open vented but recommendation is also open vented,
  // reduce the "like-for-like" bonus since performance matters more
  if (isOpenVented && isCurrentOpen && occupants >= 3) {
    // Only apply gravity pressure constraint where DHW delivery is explicitly gravity-fed.
    if (dhwDeliveryMethod === 'gravity') {
      score -= 1;
      cons.push(`Gravity-fed vented cylinder may limit shower pressure with ${occupants} occupants - unvented could improve performance if mains supply is adequate`);
      relevant.add(`cons:Gravity-fed vented constraints may limit performance with ${occupants} occupants`);
    } else if (dhwDeliveryMethod === 'pumped') {
      pros.push(`✓ Vented cylinder performance assumes pumped DHW delivery, so gravity pressure limits do not apply`);
      relevant.add('pros:✓ Vented cylinder performance assumes pumped DHW delivery');
    } else {
      cons.push(`Vented DHW delivery method is unknown - small performance uncertainty until gravity vs pumped setup is confirmed`);
      score -= 0.25;
      relevant.add('cons:Unknown DHW delivery method adds minor uncertainty');
    }
  }

  // Cost / disruption considerations
  // Open vented → unvented conversion - only penalize if minimal disruption tolerance
  if (isCurrentOpen && (newWater === "unvented" || newWater === "mixergy_unvented")) {
    if (disruptionTolerance === "one_room_only") {
      cons.push("Open-vented → unvented typically adds a notable premium for upgrade work");
      score -= 1;
      relevant.add("cons:Open-vented → unvented typically adds a notable premium for upgrade work");
    }
  }

  // Full replacement bonus for unvented systems with good mains pressure
  if (fullReplacement && mainsPressure > 2.0 && isUnvented) {
    pros.push("✓ Full replacement with excellent mains pressure makes unvented system ideal choice");
    score += 5;
    relevant.add("pros:✓ Full replacement with excellent mains pressure favors unvented");
  }

  // Stored → combi conversion
  if (newBoiler === "combi" && currentBoiler !== "combi") {
    cons.push("Converting to a combi from stored often adds a noticeable premium for pipework and cylinder removal");
    score -= 1;
    relevant.add("cons:Converting to a combi from stored often adds a noticeable premium for pipework and cylinder removal");
  }
  
  // Like-for-like advantage
  if (newBoiler === currentBoiler && newWater === currentWater) {
    pros.push("Like-for-like keeps cost and disruption low");
    score += 2;
    relevant.add("pros:Like-for-like keeps cost and disruption low");
  }

  // === NEW PREFERENCE-BASED SCORING ===

  // 1. Water Hardness Impact (especially for storage combis and certain cylinders)
  if (waterHardness === "hard" || waterHardness === "very_hard") {
    if (isStorageCombi) {
      cons.push(`⚠ Hard water area significantly increases limescale risk for storage combi - expect more frequent maintenance`);
      score -= 3;
      relevant.add("cons:⚠ Hard water increases storage combi maintenance");
    } else if (isMixergy || !isCombi) {
      pros.push(`✓ System with separate cylinder easier to maintain in hard water area - external heat exchangers accessible`);
      score += 2;
      relevant.add("pros:✓ Better for hard water maintenance");
    }
  } else if (waterHardness === "soft") {
    if (isStorageCombi) {
      pros.push(`✓ Soft water area reduces limescale buildup in storage combi`);
      score += 1;
    }
  }

  // 2. Disruption Tolerance
  if (normalizedDisruption === "low") {
    if (isCombi || isStorageCombi) {
      pros.push(`✓ Combi installation typically confined to boiler location - minimal disruption matches your preference`);
      score += 2;
      relevant.add("pros:✓ Minimal disruption installation");
    }
    if (isHeatPump) {
      cons.push(`⚠ Heat pump installation requires radiator upgrades across multiple rooms - conflicts with minimal disruption preference`);
      score -= 4;
      relevant.add("cons:⚠ Heat pump disruption conflicts with preference");
    }
  } else if (normalizedDisruption === "high") {
    if (isHeatPump) {
      pros.push(`✓ Your extensive disruption tolerance allows proper heat pump installation with optimized radiators`);
      score += 3;
      relevant.add("pros:✓ Disruption tolerance suits heat pump");
    }
  }

  // 3. Cost Priority (upfront vs ongoing) - with realistic running cost calculations
  if (costPriority === "upfront_cost") {
    if (isCombi) {
      pros.push(`✓ Combi has lowest upfront cost - aligns with your upfront cost priority`);
      score += 3;
      relevant.add("pros:✓ Low upfront cost matches priority");
    }
    if (isHeatPump) {
      cons.push(`Heat pump has high upfront cost (£3.5k-£19k after grant) - conflicts with upfront cost priority`);
      score -= 3;
      relevant.add("cons:Heat pump upfront cost conflicts with priority");
    }
    if (isMixergy) {
      cons.push(`Mixergy cylinder premium (£1-2k extra vs standard) increases upfront cost`);
      score -= 1;
    }
  } else if (costPriority === "ongoing_cost") {
    // Calculate REAL running costs accounting for electricity being 4.7× more expensive than gas
    const emitterSuitability = emitterProfile.lowTempSuitability;
    const emitterType = emitterProfile.type;
    const flowTemp = 55; // Default estimate
    
    const gasCost = calculateRunningCostPerKWh("combi", false);
    const hpStandardCost = calculateRunningCostPerKWh(newBoiler, false, emitterSuitability, emitterType, flowTemp);
    const hpTariffCost = calculateRunningCostPerKWh(newBoiler, true, emitterSuitability, emitterType, flowTemp);

    if (isHeatPump) {
      // Check if savings are meaningful (>10%)
      const standardSavings = ((gasCost.costPerKWh - hpStandardCost.costPerKWh) / gasCost.costPerKWh) * 100;
      const tariffSavings = ((gasCost.costPerKWh - hpTariffCost.costPerKWh) / gasCost.costPerKWh) * 100;

      if (solarPanels === "present" || solarPanels === "planned") {
        // Solar PV makes heat pumps much more economical
        pros.push(`✓ Heat pump running costs excellent with solar PV offsetting 30-50% of electricity - much cheaper than gas`);
        score += 4;
        relevant.add("pros:✓ Heat pump + solar = lowest running costs");
      } else if (tariffSavings >= 30) {
        // With heat pump tariff, savings are significant
        pros.push(`✓ Heat pump with dedicated tariff: ${hpTariffCost.description} vs gas: ${gasCost.description} (saves ${tariffSavings.toFixed(0)}%)`);
        cons.push(`⚠ Savings require switching to dedicated heat pump tariff (not all suppliers offer this)`);
        score += 3;
        relevant.add("pros:✓ Heat pump saves significantly with dedicated tariff");
      } else {
        // Standard tariff: savings are marginal
        cons.push(`⚠ Reality check: On standard tariff, heat pump costs ${hpStandardCost.description} vs gas ${gasCost.description} - only ${standardSavings.toFixed(0)}% savings because electricity is 4.7× gas price`);
        cons.push(`Significant savings (${tariffSavings.toFixed(0)}%) require dedicated heat pump tariff or solar PV`);
        score += 1; // Small bonus, not the big +4
        relevant.add("cons:⚠ Marginal savings on standard electricity tariff");
      }
    }
    if (isCombi || isStorageCombi) {
      const gasCostInfo = calculateRunningCostPerKWh(newBoiler, false);
      cons.push(`Gas running cost: ${gasCostInfo.description} - higher than heat pump with dedicated tariff`);
      score -= 1; // Smaller penalty since difference is not huge on standard tariffs
    }
    if (isMixergy) {
      pros.push(`✓ Mixergy suggested as an upgrade path to cut cylinder standing losses and enable smarter hot water control`);
      score += 2;
      relevant.add('pros:✓ Mixergy upgrade path reduces losses and improves smart control');
    }
  }

  // 4. Budget vs Longevity
  if (budgetVsLongevity === "longevity_focused") {
    if (isHeatPump) {
      pros.push(`✓ Heat pump lifespan (20-25 years) excellent match for longevity focus`);
      score += 3;
      relevant.add("pros:✓ Long lifespan matches longevity focus");
    }
    if (isStorageCombi) {
      cons.push(`⚠ Storage combi shorter lifespan (10-15 years) conflicts with longevity focus`);
      score -= 2;
      relevant.add("cons:⚠ Shorter lifespan conflicts with longevity focus");
    }
    if (isMixergy) {
      pros.push(`✓ Mixergy + system boiler longevity (15-25 years) aligns with longevity focus`);
      score += 2;
    }
  } else if (budgetVsLongevity === "budget_focused") {
    if (isCombi) {
      pros.push(`✓ Standard combi balances budget with adequate lifespan (12-15 years)`);
      score += 2;
      relevant.add("pros:✓ Budget-friendly option");
    }
    if (isHeatPump) {
      cons.push(`High upfront cost conflicts with budget focus (even with grants)`);
      score -= 2;
    }
  }

  // 5. Budget vs Green
  if (budgetVsGreen === "green_focused") {
    if (isHeatPump) {
      pros.push(`✓ Heat pump is greenest option - zero carbon at point of use, perfect match for green focus`);
      score += 5;
      relevant.add("pros:✓ Greenest option matches priority");
    }
    if (isCombi || isStorageCombi) {
      cons.push(`Gas boiler produces carbon emissions - conflicts with green focus`);
      score -= 3;
      relevant.add("cons:Gas emissions conflict with green priority");
    }
    if (isMixergy && (solarPanels === "present" || solarPanels === "planned")) {
      pros.push(`✓ Mixergy solar integration supports green priority with renewable energy storage`);
      score += 2;
    }
  } else if (budgetVsGreen === "budget_focused") {
    if (isCombi) {
      pros.push(`✓ Standard combi most budget-friendly option`);
      score += 2;
    }
    if (isHeatPump && budgetVsLongevity !== "longevity_focused") {
      cons.push(`Heat pump premium cost conflicts with budget focus`);
      score -= 2;
    }
  }

  // 5b. SOLAR + BATTERY ECONOMIC IMPACT
  // When solar PV and battery storage are present, electric heating economics shift dramatically
  // IMPORTANT: Only COMBI and STORAGE COMBI cannot use solar/battery (no cylinder = no immersion)
  // ALL stored water systems (system+cylinder, regular+cylinder, Mixergy) CAN use solar/battery via immersion
  if (hasSolarAndBattery) {
    const isOnDemandOnly = isCombi || isStorageCombi; // No cylinder = cannot use electric storage
    const hasStoredWater = hasStoredWaterSystem(newWater); // Has cylinder with immersion heater
    const isElectricHeating = isHeatPump || isHybrid;

    if (isOnDemandOnly) {
      // Combi and storage combi have NO CYLINDER - cannot use solar/battery for hot water
      cons.push("⚠ Combi boiler cannot utilize your solar PV + battery investment for hot water - no cylinder means no immersion heater");
      cons.push("⚠ Consider: Stored water systems (system boiler + cylinder) can use solar/battery via immersion for free hot water");
      score -= 10; // Significant penalty for not utilizing solar+battery
      relevant.add("cons:⚠ Combi cannot utilize solar + battery for hot water");
    } else if (hasStoredWater && !isHeatPump) {
      // ALL stored water systems can use immersion heater with solar/battery
      // Note: Mixergy and standard cylinders already get specific solar bonuses in their sections
      if (!isMixergy) {
        // Standard cylinders with immersion - good but not as optimized as Mixergy
        pros.push("✓ Cylinder immersion heater can use solar+battery electricity for free hot water heating");
        score += 3;
        relevant.add("pros:✓ Cylinder uses solar+battery via immersion");
      }
      // Mixergy bonus is already applied in the Mixergy section (+15 for solar+battery)
    } else if (isElectricHeating) {
      // Electric heating benefits already scored in heat pump section above
      // Just add a note about the synergy
      pros.push("✓ Electric heating fully utilizes your solar PV + battery investment for maximum savings");
    }
  } else if (hasSolar && !hasBattery) {
    // Solar only - mention opportunity for combi systems
    const isOnDemandOnly = isCombi || isStorageCombi;
    if (isOnDemandOnly) {
      cons.push("Note: Your combi boiler cannot use solar PV directly - consider stored water system to benefit from solar via immersion");
    }
  }

  // 6. Future Demand (family staying, changing needs)
  const effectiveOccupants = plannedOccupancyChange ?? occupants;
  const demandIncreasing = futureDemand === "increasing" || futureDemand === "temporary_increase" ||
                          temporaryGuests === "frequent" || temporaryGuests === "permanent_addition" ||
                          (plannedOccupancyChange && plannedOccupancyChange > occupants);

  if (demandIncreasing) {
    if (isCombi && occupants >= 3) {
      cons.push(`⚠ Increasing demand will strain combi system - simultaneous use already challenging with ${occupants} occupants`);
      score -= 3;
      relevant.add("cons:⚠ Increasing demand strains combi");
    }
    if (!isCombi && !isStorageCombi) {
      pros.push(`✓ Cylinder storage handles increasing demand well - multiple simultaneous draws no problem`);
      score += 3;
      relevant.add("pros:✓ Storage handles increasing demand");
    }
    if (isStorageCombi && effectiveOccupants <= 5) {
      pros.push(`✓ Storage combi 46L tank bridges gap for moderate demand increases`);
      score += 1;
    }
    if (temporaryGuests === "temporary_increase" || futureDemand === "temporary_increase") {
      pros.push(`Temporary demand increase manageable with scheduling - not critical for system choice`);
    }
  }

  if (futureDemand === "decreasing" || (plannedOccupancyChange && plannedOccupancyChange < occupants)) {
    if (isCombi) {
      pros.push(`✓ Decreasing demand suits combi - lower running costs with less usage`);
      score += 2;
    }
    if (isHeatPump) {
      pros.push(`✓ Heat pump efficiency excellent for decreasing demand - minimal running costs`);
      score += 1;
    }
  }

  // 7. Urgency Impact
  if (urgency === "emergency") {
    if (newBoiler === currentBoiler && newWater === currentWater) {
      pros.push(`✓ Like-for-like replacement fastest for emergency - typically 1-2 days`);
      score += 3;
      relevant.add("pros:✓ Fast emergency replacement");
    }
    if (isHeatPump) {
      cons.push(`⚠ Heat pump installation takes weeks - unsuitable for emergency replacement`);
      score -= 5;
      relevant.add("cons:⚠ Heat pump too slow for emergency");
    }
  } else if (urgency === "planning" || urgency === "future") {
    if (isHeatPump) {
      pros.push(`✓ Planning ahead allows proper heat pump installation with grants and optimal design`);
      score += 2;
      relevant.add("pros:✓ Planning time suits heat pump");
    }
    if (isMixergy) {
      pros.push(`✓ Planning time allows Mixergy integration and optimal system design`);
      score += 1;
    }
  }

  // 8. Existing System Age (if provided)
  if (existingSystemAge !== undefined) {
    if (existingSystemAge > 15) {
      if (isHeatPump) {
        pros.push(`✓ Old system (${existingSystemAge} years) good opportunity for heat pump upgrade - maximize long-term benefits`);
        score += 2;
      }
      if (newBoiler === currentBoiler) {
        cons.push(`Replacing old system (${existingSystemAge} years) with same type - consider modern alternatives`);
        score -= 1;
      }
    }
    if (existingSystemAge < 5 && newBoiler !== currentBoiler) {
      cons.push(`Current system only ${existingSystemAge} years old - changing system type increases waste`);
      score -= 1;
    }
  }

  // === FUTURE UPGRADE POSITIONING ===
  const upgradePositioning = evaluateFutureUpgradePositioning({
    currentBoiler,
    newBoiler,
    newWater
  });

  // Apply upgrade positioning score
  score += upgradePositioning.upgradeScore;

  // Add upgrade positioning insights to pros/cons
  if (upgradePositioning.upgradeScore >= 2) {
    // Good upgrade positioning
    pros.push(`✓ Strong upgrade path: preserves future flexibility for heat pumps, solar integration, and system evolution`);
    relevant.add("pros:✓ Strong upgrade path preserves future flexibility");
  } else if (upgradePositioning.upgradeScore >= 1) {
    // Moderate upgrade positioning  
    pros.push(`Moderate upgrade flexibility: some future paths remain open`);
  } else if (upgradePositioning.upgradeScore <= -2) {
    // Poor upgrade positioning
    cons.push(`⚠ Limited upgrade paths: future heat pump or solar integration will require significant additional work`);
    relevant.add("cons:⚠ Limited upgrade paths for future decarbonisation");
  }

  return {
    score,
    dailyWasteL,
    dailyOverheadKWh,
    dailyCylinderLoss,
    pros,
    cons,
    relevant,
    futureUpgradePositioning: upgradePositioning.upgradeText
  };
}

/**
 * Generate a short "why" explanation for an option
 */
function makeWhyText(boiler: string, _water: string, occupants: number, bathrooms: number): string {
  if (boiler === "air_source_hp" || boiler === "ground_source_hp") {
    return "Heat pumps offer 400-500% efficiency, zero-carbon heating, and long-term running cost savings, especially with solar PV integration.";
  } else if (boiler === "hybrid") {
    return "Hybrid systems combine heat pump efficiency with gas boiler reliability for peak demand.";
  } else if (boiler === "storage_combi") {
    if (occupants <= 4 && bathrooms <= 2) {
      return "Storage combi bridges the gap between standard combi and system boiler, offering higher flow rates with space-saving design.";
    }
    return "Storage combi provides better hot water flow than standard combi, though larger households may benefit from a full cylinder.";
  } else if (boiler === "combi") {
    if (occupants <= 3 && bathrooms <= 2) {
      return "Works for your size household, but expect some hot-water lag and per-draw energy overhead.";
    }
    return "Will work, but your usage suggests stored water would reduce lag and wasted water.";
  } else {
    return "Stored hot water suits your household because it reduces wait time and copes better with multiple uses.";
  }
}

/**
 * Format boiler name for display
 */
function formatBoilerName(value: string): string {
  const boilerLabels: Record<string, string> = {
    regular: "Regular",
    system: "System boiler",
    combi: "Combi",
    storage_combi: "Storage combi",
    air_source_hp: "ASHP",
    ground_source_hp: "GAHP",
    hybrid: "Hybrid system"
  };
  return boilerLabels[value] || value;
}

/**
 * Format water system name for display
 * Supports both old string format and new strategy+variant model
 */
function formatWaterName(value: string | { strategy: string; variant?: string }): string {
  // Handle new strategy+variant format
  if (typeof value === 'object' && 'strategy' in value) {
    const { strategy, variant = 'standard' } = value;
    const baseLabels: Record<string, string> = {
      open_vented: "open vented",
      unvented: "unvented",
      thermal_store: "Thermal store",
      heat_battery: "Heat battery (Sunamp)",
      storage_combi: "small store/on demand",
      on_demand: "on demand",
      gravity: "Gravity fed"
    };
    
    const baseName = baseLabels[strategy] || strategy.replace(/_/g, " ");
    
    // Add variant prefix for Mixergy
    if (variant === 'mixergy') {
      return `Mixergy ${baseName}`;
    }
    
    return baseName;
  }
  
  // Handle legacy string format
  const waterLabels: Record<string, string> = {
    open_vented: "open vented",
    mixergy_open: "Mixergy open vented",
    unvented: "unvented",
    mixergy_unvented: "Mixergy unvented",
    thermal_store: "Thermal store",
    heat_battery: "Heat battery (Sunamp)",
    storage_combi: "small store/on demand",
    on_demand: "on demand",
    gravity: "Gravity fed"
  };
  return waterLabels[value] || value.replace(/_/g, " ");
}

/**
 * Determine system type from boiler and water combination
 */
function determineSystemType(boiler: string, water: string): SystemType {
  if (boiler === "air_source_hp" || boiler === "ground_source_hp") {
    return "heat_pump";
  }
  if (boiler === "hybrid") {
    return "hybrid";
  }
  if (boiler === "combi" || boiler === "storage_combi" || water === "on_demand") {
    return "combi";
  }
  if (boiler === "system") {
    return "system";
  }
  if (boiler === "regular") {
    return "regular";
  }
  return "unknown";
}

/**
 * Calculate confidence score based on data quality
 * Confidence reflects data quality, not system choice
 * 
 * Rules:
 * - Known heat loss: +25%
 * - Each assumed Class-B input: -10%
 * - Gas assumed: -5%
 * - Radiator suitability unknown: -10%
 * 
 * Display bands:
 * - High ≥ 85%
 * - Medium 65–84%
 * - Low < 65%
 */
function calculateConfidenceScore(input: SystemRecInput, heatLossResult: { useKnownHeatLoss: boolean }): {
  score: number;
  band: "low" | "medium" | "high";
  assumptions: string[];
} {
  let score = 100; // Start at 100%
  const assumptions: string[] = [];
  
  // Known heat loss: +25%
  if (heatLossResult.useKnownHeatLoss) {
    score += 25;
    assumptions.push("Known heat loss provided (+25% confidence)");
  }
  
  // Each assumed Class-B input: -10%
  // Class-B inputs are important but not critical
  if (!input.propertyType) {
    score -= 10;
    assumptions.push("Property type assumed (-10% confidence)");
  }
  
  if (input.occupancy === undefined) {
    score -= 10;
    assumptions.push("Occupancy assumed (-10% confidence)");
  }
  
  if (input.bathrooms === undefined) {
    score -= 10;
    assumptions.push("Bathroom count assumed (-10% confidence)");
  }
  
  if (!input.propertyAgeBand && !heatLossResult.useKnownHeatLoss) {
    score -= 10;
    assumptions.push("Property age assumed for heat loss calculation (-10% confidence)");
  }
  
  // Gas assumed: -5%
  if (input.gasAvailable === undefined) {
    score -= 5;
    assumptions.push("Gas availability assumed (-5% confidence)");
  }
  
  // Radiator suitability unknown: -10%
  if (!input.radiatorSuitability || input.radiatorSuitability === "unknown") {
    score -= 10;
    assumptions.push("Radiator suitability not confirmed (-10% confidence)");
  }
  
  // Clamp score to 0-100 range (125 was max with +25% boost, but we normalize to 100 for band calculation)
  score = Math.max(0, Math.min(100, score));
  
  // Determine band
  let band: "low" | "medium" | "high";
  if (score >= 85) {
    band = "high";
  } else if (score >= 65) {
    band = "medium";
  } else {
    band = "low";
  }
  
  return { score, band, assumptions };
}

/**
 * Generate opening sentence
 * Note: Currently the same for all confidence levels per spec,
 * but function structure allows for future variants if needed
 */
function generateOpeningSentence(_confidence: "low" | "medium" | "high"): string {
  return "Based on what we know about your home today, this is the system we would choose.";
}

/**
 * Generate confidence-aware explanation
 */
function generateConfidenceExplanation(confidence: "low" | "medium" | "high"): string {
  switch (confidence) {
    case "high":
      return "This system fits your home well and meets your heating and hot water needs with minimal risk.";
    case "medium":
      return "Based on the information available today, this is the most balanced option for your home.";
    case "low":
      return "This is our best recommendation based on limited information today.";
  }
}

/**
 * Generate reason buckets for why this system won
 * Select 3-4 reasons based on the system characteristics
 */
function generateWhyThisWon(
  boiler: string,
  water: string,
  pros: string[],
  futureUpgradePositioning: string
): string[] {
  const reasons: string[] = [];
  const reasonPool = [
    { text: "Fits the home well", keywords: ["suitable", "appropriate", "fits", "match"] },
    { text: "Avoids unnecessary disruption", keywords: ["minimal", "disruption", "pipework", "installation"] },
    { text: "Delivers reliable hot water", keywords: ["hot water", "flow", "cylinder", "reliable", "simultaneous"] },
    { text: "Keeps future options open", keywords: ["future", "upgrade", "flexibility", "options"] },
    { text: "Offers good long-term value", keywords: ["cost", "value", "longevity", "lifespan", "savings"] },
    { text: "Avoids high upfront risk", keywords: ["upfront", "risk", "budget", "affordable"] }
  ];
  
  // Check pros and future upgrade positioning for relevant reasons
  const allText = [...pros, futureUpgradePositioning].join(" ").toLowerCase();
  
  for (const reason of reasonPool) {
    const matches = reason.keywords.some(keyword => allText.includes(keyword));
    if (matches && reasons.length < 4) {
      reasons.push(reason.text);
    }
  }
  
  // Ensure we have at least 3 reasons
  while (reasons.length < 3 && reasons.length < reasonPool.length) {
    const remainingReasons = reasonPool.filter(r => !reasons.includes(r.text));
    if (remainingReasons.length > 0) {
      reasons.push(remainingReasons[0].text);
    } else {
      break;
    }
  }
  
  return reasons.slice(0, 4);
}

/**
 * Generate heat pump framing (when heat pump is not recommended)
 * Only shown for non-heat-pump alternatives, never for the primary recommendation
 */
function generateHeatPumpFraming(isHeatPump: boolean, isPrimary: boolean): string | undefined {
  // Never show framing for heat pump systems or primary recommendation
  if (isHeatPump || isPrimary) {
    return undefined;
  }
  
  // Show physics-based framing for non-heat-pump alternatives only
  return "A heat pump is technically possible here, but based on your current emitters and insulation, it would require higher flow temperatures (reducing efficiency) or significant emitter upgrades. The recommended system better matches your property's current configuration.";
}

/**
 * Generate canonical closing line
 */
function generateClosingLine(isPrimary: boolean): string {
  if (isPrimary) {
    return "If this were our home, this is the system we'd choose.";
  }
  return "This is the option we'd be comfortable standing behind.";
}

/**
 * Legacy confidence function for backwards compatibility
 * @deprecated Use calculateConfidenceScore instead
 */
function determineConfidence(score: number, hasCompleteInputs: boolean): "low" | "medium" | "high" {
  if (!hasCompleteInputs) {
    return "low";
  }
  if (score >= 5) {
    return "high";
  }
  if (score >= 2) {
    return "medium";
  }
  return "low";
}

/**
 * Main computation function - pure logic with no side effects
 * 
 * @param input - System recommendation input parameters
 * @returns Structured recommendation output with primary and alternative options
 */
export function computeSystemRecommendation(input: SystemRecInput): SystemRecOutput {
  const isDeterminate = !!(
    input.mainsPressure &&
    input.flowRate &&
    input.hotWaterDemand !== 'unknown'
  );
  // Derive retrofit metrics from raw input (emitters + insulation profile)
  const { emitterProfile, retrofitProfile, retrofitLevel, lowTempReadiness } = deriveRetrofitMetrics(input);
  
  // Initialize evidence trace for observability
  const evidence: import('./types').ComputeEvidence = {
    appliedInputs: {},
    missingInputs: [],
    fallbacksUsed: [],
    rejections: []
  };

  // Track critical inputs only (GC number, make/model, solar, etc.)
  if (input.currentSystemEfficiency?.gcNumber) {
    evidence.appliedInputs.gcNumber = input.currentSystemEfficiency.gcNumber;
  }
  if (input.currentSystemEfficiency?.boilerMake) {
    evidence.appliedInputs.boilerMake = input.currentSystemEfficiency.boilerMake;
  }
  if (input.currentSystemEfficiency?.boilerModel) {
    evidence.appliedInputs.boilerModel = input.currentSystemEfficiency.boilerModel;
  }
  if (input.solarPanels) {
    evidence.appliedInputs.solarPanels = input.solarPanels;
  }
  if (input.batteryStorage) {
    evidence.appliedInputs.batteryStorage = input.batteryStorage;
  }

  // Migration: Convert old water type strings to new strategy + variant model
  // This maintains backward compatibility with existing tests and UI
  function migrateWaterType(waterType: string | undefined): { strategy: string; variant: string } {
    if (!waterType) {
      return { strategy: 'on_demand', variant: 'standard' };
    }
    
    // Handle old Mixergy string types
    if (waterType === 'mixergy_unvented') {
      return { strategy: 'unvented', variant: 'mixergy' };
    }
    if (waterType === 'mixergy_open') {
      return { strategy: 'open_vented', variant: 'mixergy' };
    }
    
    // Cylinder strategies default to standard variant
    if (waterType === 'open_vented' || waterType === 'unvented') {
      return { strategy: waterType, variant: 'standard' };
    }
    
    // Other types don't have variants
    return { strategy: waterType, variant: 'standard' };
  }

  // 1. Normalize inputs with defaults
  const houseType = input.propertyType || "semi";
  // Note: bedrooms reserved for future scoring enhancements
  const bathrooms = input.bathrooms ?? 2;
  const occupants = input.occupancy ?? 3;
  // Intelligent draws per day estimation - extracted to helper function for testability
  const drawsPerDay = input.drawsPerDay ?? calculateEstimatedDrawsPerDay(input.occupancy, input.bathrooms);
  const currentBoiler = input.currentBoiler || "regular";
  const currentWater = input.currentWater || "open_vented";
  
  // Use canonical cold mains selector to get actual or default values
  const coldMainsData = selectColdMains(input);
  const coldMainsEffective = getColdMainsWithDefaults(input);
  const mainsPressure = coldMainsEffective.staticPressureBar;
  const flowRate = coldMainsEffective.flowLpm;
  
  // Gas availability - derive from current boiler type if not explicitly provided
  // If currentBoiler indicates gas (combi/system/regular/storage_combi), then gas is available
  const gasAvailable = input.gasAvailable ?? isGasBoiler(currentBoiler);

  // Calculate heat loss (with known heat loss override support)
  const heatLossResult = calculateHeatLoss(input);
  const heatLossKw = heatLossResult.heatLossKw;

  // Run simple whole-house heat loss engine when SimpleHeatLossInputs are provided
  const simpleHeatLossResult: SimpleHeatLossResult | null =
    input.simpleHeatLossInputs
      ? calculateSimpleWholeHouseHeatLoss(input.simpleHeatLossInputs)
      : null;

  // Run room-by-room engine when room data is provided (takes precedence over simple model)
  const wholeHouseHeatLossResult = input.heatLossRooms && input.heatLossRooms.length > 0
    ? calculateWholeHouseHeatLoss(
        input.heatLossRooms,
        input.heatLossDesignTempInternalC ?? 21,
        input.heatLossDesignTempExternalC ?? -3
      )
    : null;
  const heatLossSummary = wholeHouseHeatLossResult
    ? buildHeatLossSummary(wholeHouseHeatLossResult)
    : null;

  // Best available heat loss kW (room-by-room > simple > inferred)
  const preciseSizingKw: number | null =
    wholeHouseHeatLossResult?.totalHeatLossKW ??
    simpleHeatLossResult?.sizingTargetKW ??
    null;

  // Preference inputs
  const spaceAvailability = input.spaceAvailability || "medium";
  const disruptionTolerance = input.disruptionTolerance || "moderate";
  const fullReplacement = input.fullReplacement ?? false;
  const temperaturePreference = input.temperaturePreference || "comfort";
  const solarPanels = input.solarPanels || "none";
  const batteryStorage = input.batteryStorage || "none";
  const electricalCapacity = input.electricalCapacityConfidence || "unknown";
  const heatEmitterType = input.heatEmitterType || "standard_rads";

  // === SOLAR + BATTERY SYNERGY FLAGS ===
  // When both solar PV and battery storage are present, electric heating economics shift dramatically
  const hasSolar = solarPanels === "present";
  const hasBattery = batteryStorage === "present";
  const hasSolarAndBattery = hasSolar && hasBattery;
  const planningSolar = solarPanels === "planned";
  const planningBattery = batteryStorage === "planned";
  const planningSolarOrBattery = planningSolar || planningBattery;

  // Self-consumption rates for cost calculations:
  // - Solar only: ~30% self-consumption (rest exported)
  // - Solar + Battery: ~70-80% self-consumption (battery stores excess for later use)
  const solarSelfConsumptionRate = hasSolarAndBattery ? 0.75 : (hasSolar ? 0.30 : 0);

  // Effective electricity cost reduction for heat pumps and electric heating
  // This represents the weighted average of "free" solar electricity vs grid electricity
  const electricCostReductionFactor = hasSolarAndBattery ? 0.40 : (hasSolar ? 0.15 : 0);
  const acceptsVisiblePipework = input.acceptsVisiblePipework ?? true;
  const wallSpaceForOversizedRads = input.wallSpaceForOversizedRads ?? true;
  const insulationLevel = input.insulationLevel || "unknown";
  const plannedInsulationUpgrades = input.plannedInsulationUpgrades ?? false;
  const numRecommendations = input.numRecommendations ?? 2;

  // Financial priorities
  const costPriority = input.costPriority || "balanced";
  const budgetVsLongevity = input.budgetVsLongevity || "balanced";
  const budgetVsGreen = input.budgetVsGreen || "balanced";

  // Household dynamics
  const futureDemand = input.futureDemand || "stable";
  const temporaryGuests = input.temporaryGuests || "none";
  const plannedOccupancyChange = input.plannedOccupancyChange;
  const occupancyTiming = input.occupancyTiming || "unknown";

  // Property characteristics
  const waterHardness = input.waterHardness || "unknown";
  const existingSystemAge = input.existingSystemAge;
  const urgency = input.urgency || "planning";

  // Original user-specified options (for backwards compatibility)
  const newABoiler = input.newABoiler || "combi";
  const newAWater = input.newAWater || "on_demand";
  const newBBoiler = input.newBBoiler || "system";
  const newBWater = input.newBWater || "unvented";

  // Additional option specifications for C, D, E (only if explicitly provided)
  const newCBoiler = (input as any).newCBoiler;
  const newCWater = (input as any).newCWater;
  const newDBoiler = (input as any).newDBoiler;
  const newDWater = (input as any).newDWater;
  const newEBoiler = (input as any).newEBoiler;
  const newEWater = (input as any).newEWater;

  // 2. Check if we have complete inputs (not using many defaults)
  const hasCompleteInputs = !!(
    input.propertyType &&
    input.occupancy !== undefined &&
    input.bathrooms !== undefined &&
    input.drawsPerDay !== undefined &&
    input.mainsPressure !== undefined &&
    input.flowRate !== undefined
  );

  // Constants for excluded water system types
  const EXCLUDED_WATER_SYSTEMS = ['thermal_store'];

  const hasStoredDhwContext = input.dhwArchitecture === 'stored-dhw' ||
    ['open_vented', 'mixergy_open', 'unvented', 'mixergy_unvented', 'gravity'].includes(currentWater);
  const standingLossBand = input.standingLossBand ?? 'unknown';
  const mixergyPriorityKeys = new Set(['smart_controls', 'future_proofing', 'low_running_cost', 'hot_water_performance']);
  const hasMixergyPrioritySignal = (input.upgradePriorities || []).some((priority) => mixergyPriorityKeys.has(priority.key));
  const hasStandingLossSignal = standingLossBand === 'high' || standingLossBand === 'medium';
  const hasCylinderSpaceForMixergy = input.cylinderSpace !== false;

  // Mixergy should be offered as an upgrade path based on need signals, not current cylinder brand/profile.
  const smartCylinderAllowed = hasStoredDhwContext && hasCylinderSpaceForMixergy &&
    (hasStandingLossSignal || hasMixergyPrioritySignal || input.hotWater?.variant === 'mixergy' || numRecommendations > 5);
  
  // Generate extra regular boiler cylinder options (F, G) when appropriate
  // Only add these if user is requesting multiple recommendations or hasn't specified custom options
  const userSpecifiedCustomOptions = input.newABoiler || input.newBBoiler;
  const compareOptionsProvided = Array.isArray(input.compareOptions) && input.compareOptions.length > 0;
  const shouldGenerateExtraOptions = compareOptionsProvided
    ? input.compareOptionsAutoExpand === true
    : numRecommendations > 2 || !userSpecifiedCustomOptions;
  
  const extraOptionConfigs = [];
  
  // Only add extra options if user hasn't specified custom options A/B
  // This ensures user-specified options are prioritized in scoring/ranking
  if (shouldGenerateExtraOptions && !userSpecifiedCustomOptions) {
    // F, G: Regular boiler with standard cylinders
    extraOptionConfigs.push({ id: "F", boiler: "regular", water: "open_vented" });
    extraOptionConfigs.push({ id: "G", boiler: "regular", water: "unvented" });
    
    // Add System boiler - open vented (required by UI specification)
    extraOptionConfigs.push({ id: "P", boiler: "system", water: "open_vented" });
    
    // Add Storage combi (required by UI specification)
    extraOptionConfigs.push({ id: "Q", boiler: "storage_combi", water: "storage_combi" });
  }
  
  // Add Mixergy variants when generating extra options and user hasn't specified custom options
  if (shouldGenerateExtraOptions && smartCylinderAllowed && !userSpecifiedCustomOptions) {
    extraOptionConfigs.push({ id: "H", boiler: "regular", water: "mixergy_open" });
    extraOptionConfigs.push({ id: "I", boiler: "regular", water: "mixergy_unvented" });
    extraOptionConfigs.push({ id: "J", boiler: "system", water: "mixergy_open" });
    extraOptionConfigs.push({ id: "K", boiler: "system", water: "mixergy_unvented" });
  }

  if (!compareOptionsProvided) {
    extraOptionConfigs.push({ id: "L", boiler: "air_source_hp", water: "unvented" });
    extraOptionConfigs.push({ id: "M", boiler: "ground_source_hp", water: "unvented" });
    if (smartCylinderAllowed) {
      extraOptionConfigs.push({ id: "N", boiler: "air_source_hp", water: "mixergy_unvented" });
      extraOptionConfigs.push({ id: "O", boiler: "ground_source_hp", water: "mixergy_unvented" });
      // Add open vented Mixergy variants for heat pumps
      extraOptionConfigs.push({ id: "R", boiler: "air_source_hp", water: "mixergy_open" });
      extraOptionConfigs.push({ id: "S", boiler: "ground_source_hp", water: "mixergy_open" });
    }
  }

  // 3. Define all possible options
  const baseOptionConfigs = compareOptionsProvided
    ? input.compareOptions!.map((config, index) => ({
      id: config.id ?? String.fromCharCode(65 + index),
      boiler: config.boiler,
      water: config.water
    }))
    : [
      { id: "A", boiler: newABoiler, water: newAWater },
      { id: "B", boiler: newBBoiler, water: newBWater },
    ];
  
  // Add C, D, E only if explicitly specified
  if (!compareOptionsProvided) {
    if (newCBoiler && newCWater) {
      baseOptionConfigs.push({ id: "C", boiler: newCBoiler, water: newCWater });
    }
    if (newDBoiler && newDWater) {
      baseOptionConfigs.push({ id: "D", boiler: newDBoiler, water: newDWater });
    }
    if (newEBoiler && newEWater) {
      baseOptionConfigs.push({ id: "E", boiler: newEBoiler, water: newEWater });
    }
  }
  
  const optionConfigs = baseOptionConfigs
    .concat(extraOptionConfigs)
    .filter(config => {
    // EXCLUDE thermal stores and any other excluded water system types from recommendations
    // They may appear as existing systems but never as recommendations
    return !EXCLUDED_WATER_SYSTEMS.includes(config.water);
  });
  
  // Deduplicate options using Map for O(n) performance
  type OptionConfig = { id: string; boiler: string; water: string };
  const uniqueOptions = new Map<string, OptionConfig>();
  optionConfigs.forEach(config => {
    const key = `${config.boiler}|${config.water}`;
    if (!uniqueOptions.has(key)) {
      uniqueOptions.set(key, config);
    }
  });
  const deduplicatedConfigs = Array.from(uniqueOptions.values());

  // Common evaluation parameters
  const evalParams = {
    houseType,
    occupants,
    bathrooms,
    drawsPerDay,
    currentBoiler,
    currentWater,
    mainsPressure,
    flowRate,
    gasAvailable,
    spaceAvailability,
    disruptionTolerance,
    fullReplacement,
    temperaturePreference,
    solarPanels,
    batteryStorage,
    electricalCapacity,
    heatEmitterType,
    acceptsVisiblePipework,
    wallSpaceForOversizedRads,
    insulationLevel,
    plannedInsulationUpgrades,
    costPriority,
    budgetVsLongevity,
    budgetVsGreen,
    futureDemand,
    temporaryGuests,
    plannedOccupancyChange,
    occupancyTiming,
    waterHardness,
    existingSystemAge,
    urgency,
    dhwDeliveryMethod: input.dhwDeliveryMethod || 'unknown',
    retrofitLevel,
    lowTempReadiness,
    emitterProfile,
    retrofitProfile
  };

  // 4. Evaluate all options and build recommendation list
  // CRITICAL FIX: Evaluate ALL option configs first, THEN sort and filter by numRecommendations
  // This ensures we don't miss better options (like heat pumps) that come later in the list
  const options: RecommendationOption[] = [];

  for (let i = 0; i < deduplicatedConfigs.length; i++) {
    const config = deduplicatedConfigs[i];

    // First, check if this option is fundamentally unsuitable
    const suitability = evaluateSystemSuitability({
      boiler: config.boiler,
      water: config.water,
      bathrooms,
      occupants,
      flowRate,
      mainsPressure,
      coldMainsCertainty: coldMainsData.certainty,
      urgency,
      costPriority,
      budgetVsGreen,
      budgetVsLongevity,
      disruptionTolerance,
      acceptsVisiblePipework,
      wallSpaceForOversizedRads,
      heatEmitterType,
      insulationLevel,
      plannedInsulationUpgrades
    });

    // Then evaluate the option's performance characteristics
    const evaluation = evaluateOption({
      ...evalParams,
      newBoiler: config.boiler,
      newWater: config.water
    });

    // Apply penalty score and add exclusion reason to cons
    let finalScore = evaluation.score + suitability.penaltyScore;
    const finalCons = [...evaluation.cons];
    
    // Apply heat pump confidence penalty for unknown critical inputs and flow temp requirements
    const isHeatPump = config.boiler === 'air_source_hp' || config.boiler === 'ground_source_hp';
    if (isHeatPump) {
      const heatLossKwForHp = input.heatLossKw || input.knownHeatLossKw;
      const hpPenalty = getScorePenalty(input, heatLossKwForHp);
      finalScore -= hpPenalty.penalty;
      
      // Add penalty reasons to cons if they're not already mentioned
      for (const reason of hpPenalty.reasons) {
        if (!finalCons.some(con => con.includes(reason))) {
          finalCons.push(`⚠ ${reason}`);
        }
      }
    }

    // === HEAT LOSS SIZING PENALTIES ===
    // Applied when room-by-room or simple whole-house heat loss data is available.
    // Simple model confidence penalties are also applied here.
    if (wholeHouseHeatLossResult || simpleHeatLossResult) {
      // Room-by-room result takes precedence over simple model
      const sizingHeatLossKw = wholeHouseHeatLossResult
        ? wholeHouseHeatLossResult.totalHeatLossKW
        : simpleHeatLossResult!.totalHeatLossKW;

      /**
       * Sizing thresholds (BS EN 12831 / CIBSE guidance):
       *   DHW_HEADROOM_FACTOR  (1.2)  — minimum 20% overhead for domestic hot water peak demand
       *   UNDERSIZED_THRESHOLD (0.85) — flag when heat loss exceeds 85% of nominal output
       *                                 (leaves only 15% headroom, insufficient for DHW peaks)
       *   OVERSIZED_THRESHOLD  (0.4)  — flag when heat loss is below 40% of nominal output
       *                                 (boiler is running at <40% load, cycling risk is high)
       *   HEAT_PUMP_LOW_LOAD_KW (8)   — properties below 8 kW are well-suited to low-temperature
       *                                 heat pump systems without emitter upgrades
       */
      const DHW_HEADROOM_FACTOR = 1.2;
      const UNDERSIZED_THRESHOLD = 0.85;
      const OVERSIZED_THRESHOLD = 0.4;
      const HEAT_PUMP_LOW_LOAD_KW = 8;

      // Estimate nominal boiler output from heat loss (round to 5 kW increment + DHW headroom)
      const nominalOutputKw = Math.max(
        Math.ceil(sizingHeatLossKw / 5) * 5,
        sizingHeatLossKw * DHW_HEADROOM_FACTOR
      );

      const isGasOrOilBoiler = ['combi', 'system', 'regular', 'storage_combi'].includes(config.boiler);
      if (isGasOrOilBoiler && nominalOutputKw > 0) {
        if (sizingHeatLossKw > nominalOutputKw * UNDERSIZED_THRESHOLD) {
          // Design heat loss is close to or above the headroom limit — undersizing risk
          finalScore -= 15;
          if (!finalCons.some(con => con.includes('undersized'))) {
            finalCons.push(`⚠ Design heat loss (${sizingHeatLossKw.toFixed(1)} kW) is close to system output limit — verify sizing before specification`);
          }
        } else if (sizingHeatLossKw < nominalOutputKw * OVERSIZED_THRESHOLD) {
          // Boiler is significantly oversized — cycling and inefficiency risk
          finalScore -= 8;
          if (!finalCons.some(con => con.includes('oversized'))) {
            finalCons.push(`⚠ System may be significantly oversized for this heat loss (${sizingHeatLossKw.toFixed(1)} kW) — consider a smaller output`);
          }
        }
      }

      // Heat pump suitability boost — low heat loss makes low-temperature systems viable
      if (isHeatPump && sizingHeatLossKw <= HEAT_PUMP_LOW_LOAD_KW) {
        finalScore += 10;
      }

      // Apply confidence penalties when using simple model
      if (simpleHeatLossResult && !wholeHouseHeatLossResult) {
        const conf = simpleHeatLossResult.confidence0to100;
        if (conf < 60) {
          finalScore -= 12;
          if (!finalCons.some(con => con.includes('Sizing confidence low'))) {
            finalCons.push('⚠ Sizing confidence low — verify inputs before final specification.');
          }
        } else if (conf < 80) {
          finalScore -= 5;
        }

        // Replace vague "heat loss unknown" with quantified sizing note
        const target = simpleHeatLossResult.sizingTargetKW.toFixed(1);
        const hl = simpleHeatLossResult.totalHeatLossKW.toFixed(1);
        const ext = Math.round(simpleHeatLossResult.designTempExternalC);
        if (!finalCons.some(con => con.includes('Design heat loss'))) {
          finalCons.push(
            `Design heat loss: ${hl} kW at ${ext}°C. Recommended sizing target: ${target} kW (includes 20% headroom).`
          );
        }
      }
    } else {
      // Penalize options when no heat loss model is available.
      finalScore -= 10;
      if (!finalCons.some(con => con.toLowerCase().includes('heat loss calculation required'))) {
        finalCons.push('Heat loss calculation required before confirming output size.');
      }
    }

    // Clamp final score to 0-100 range to prevent negative or excessive values
    // Large negative penalties indicate hard exclusions, but score should stay in valid range
    finalScore = Math.max(0, Math.min(100, finalScore));

    // Add Y-Plan weakness if heat loss > 18kW (aligned with fitScoring threshold)
    const heatLoss = input.heatLossKw || input.knownHeatLossKw;
    const waterLabelLower = formatWaterName(config.water).toLowerCase();
    const isYPlan = waterLabelLower.includes('y-plan') || 
                    input.distribution?.systemLayout === 'y_plan';
    
    if (isYPlan && heatLoss && heatLoss > 18) {
      const weaknessMsg = `⚠ Y-Plan becomes a limitation above ${Math.round(heatLoss)}kW - S-Plan recommended for better zoning at this heat load`;
      if (!finalCons.some(con => con.includes('Y-Plan') && con.includes('limitation'))) {
        finalCons.push(weaknessMsg);
      }
    }

    // Put exclusion reason at the TOP of cons for visibility
    if (suitability.excluded && suitability.reason) {
      finalCons.unshift(suitability.reason);
      // Track rejection in evidence
      evidence.rejections.push({
        optionId: config.id,
        reason: suitability.reason,
        detail: `Boiler: ${config.boiler}, Water: ${config.water}`
      });
    }

    // Compute LCOO if input provided
    const lcooInput = (input as any).lcoo as LcooInput | undefined;
    const lcoo = computeLcooForOption(config.id, config.boiler, config.water, input, lcooInput);

    // Calculate longevity metrics
    const analysisPeriodYears = lcooInput?.periodYears || 20;
    const systemType = determineSystemType(config.boiler, config.water);
    const longevity = calculateLongevity(
      systemType,
      config.boiler,
      analysisPeriodYears,
      undefined // New system, no age
    );

    // Generate visual model for system component visualization
    const visualModel = generateVisualModel(
      config.id,
      systemType,
      config.boiler,
      config.water,
      heatLossKw,
      flowRate,
      retrofitLevel,
      lowTempReadiness
    );

    // Smart controls are included in every recommendation by policy.
    // Mixergy options are therefore also always represented as smart-enabled.
    const includesSmartControl = true;

    // Build SystemBlueprint and EvaluationContext for this option
    // This provides the structured "truth" layer for scoring
    let blueprintContext: EvaluationContext | undefined;
    try {
      const blueprint = blueprintFromLegacyConfig(config.boiler, config.water, {
        controls: {
          zoning: input.distribution?.systemLayout === 's_plan' ? 'splan' : 'yplan',
          smart: includesSmartControl,
        },
        distribution: {
          primariesMm: input.distribution?.primaryPipeSize === '28mm' ? 28
            : input.distribution?.primaryPipeSize === '22mm' ? 22
            : undefined,
        },
      });
      blueprintContext = buildEvaluationContext(blueprint, input);
    } catch {
      // Blueprint construction is best-effort; legacy path continues working
      blueprintContext = undefined;
    }

    options.push({
      id: config.id,
      title: `${formatBoilerName(config.boiler)} - ${formatWaterName(config.water)}`,
      boiler: config.boiler,
      boilerLabel: formatBoilerName(config.boiler),
      water: config.water,
      waterLabel: formatWaterName(config.water),
      systemType: systemType,
      summary: makeWhyText(config.boiler, config.water, occupants, bathrooms),
      why: makeWhyText(config.boiler, config.water, occupants, bathrooms),
      ...evaluation,
      score: finalScore,
      cons: finalCons,
      confidence: determineConfidence(finalScore, hasCompleteInputs),
      excluded: suitability.excluded,
      exclusionReason: suitability.reason,
      lcoo,
      longevity,
      visualModel,
      includesSmartControl,
      // Attach blueprint context for downstream consumers (presentation, scoring extensions)
      ...(blueprintContext ? { _evaluationContext: blueprintContext } : {}),
    });
  }

  // 4b. Apply value weighting modifier if enabled
  const lcooCfg = (input as any).lcoo as LcooInput | undefined;
  
  if (lcooCfg?.valueWeighting?.enabled) {
    const strengthPct = lcooCfg.valueWeighting.strengthPct ?? 10;

    // Collect valid annualized costs from options where LCOO exists
    const costs = options
      .map(o => o.lcoo?.annualizedCostGBP)
      .filter((n): n is number => typeof n === "number" && isFinite(n));

    if (costs.length >= 2) {
      const minCost = Math.min(...costs);
      const maxCost = Math.max(...costs);

      for (const o of options) {
        const annual = o.lcoo?.annualizedCostGBP;
        if (typeof annual === "number" && isFinite(annual)) {
          const mod = computeValueModifier(annual, minCost, maxCost, strengthPct);
          (o as any).valueModifier = mod;
          (o as any).scoreBeforeValue = o.score;
          o.score = clamp(o.score + mod, 0, 10);

          // Attach explanation for UI
          (o as any).valueNotes = [
            `Value weighting enabled (${clamp(strengthPct, 0, 25)}%).`,
            `Annualized cost: £${Math.round(annual).toLocaleString()}/yr (min £${Math.round(minCost).toLocaleString()}, max £${Math.round(maxCost).toLocaleString()}).`,
            `Score modifier: ${mod >= 0 ? "+" : ""}${mod.toFixed(2)}`
          ];
        } else {
          (o as any).valueModifier = 0;
          (o as any).valueNotes = [`Value weighting enabled, but no pricing data provided for this option.`];
        }
      }
    } else {
      // Not enough priced options to compare
      for (const o of options) {
        (o as any).valueModifier = 0;
        (o as any).valueNotes = [`Value weighting enabled, but fewer than 2 options have whole-life costs.`];
      }
    }
  }

  // 4b. Compute fit scores for priorities and future plans (if provided)
  if (input.upgradePriorities || input.futurePlans) {
    for (const option of options) {
      const fitScore = computeOptionFitScore(option, input);
      option.fitScore = fitScore;
      option.scoreLabel = fitScore.breakdown.scoreLabel;
      option.retrofitScore = fitScore.breakdown.retrofitScore;
      option.redesignedScore = fitScore.breakdown.redesignedScore;
      option.architectureLevel = fitScore.breakdown.architectureLevel;
      option.redesignRequirements = fitScore.breakdown.redesignRequirements;
    }
  }

  // 4c. Compute running-cost costing data for each option
  for (const option of options) {
    option.costing = computeOptionCosting(option, input);
  }

  // 5. Sort by score descending (best first)
  const optionsAll = options;
  const optionsRanked = [...optionsAll].sort((a, b) => {
    // If fitScores exist, use overall fit score; otherwise use base score
    const scoreA = a.fitScore?.overall ?? a.score;
    const scoreB = b.fitScore?.overall ?? b.score;
    return scoreB - scoreA;
  });

  // 5b. Limit to top N recommendations after sorting
  // This ensures we get the BEST options, not just the first N from the hardcoded list
  const topOptions = optionsRanked.slice(0, numRecommendations);

  // 6. Calculate overall confidence
  const confidenceResult = calculateConfidenceScore(input, heatLossResult);

  // 6. Build assumptions list
  const assumptions: string[] = [];
  
  // Add heat loss assumptions
  assumptions.push(...heatLossResult.assumptions);
  
  if (!input.propertyType) {
    assumptions.push(`Property type assumed as "${houseType}"`);
  }
  if (input.occupancy === undefined && input.bathrooms === undefined) {
    // Only show as assumption if we have NEITHER occupancy NOR bathrooms
    assumptions.push(`Occupancy assumed as ${occupants} people`);
  }
  if (input.drawsPerDay === undefined && input.occupancy === undefined && input.bathrooms === undefined) {
    // Only show as assumption if we calculated it with no real data
    assumptions.push(`Hot water draws per day assumed as ${drawsPerDay}`);
  }
  // Only add water pressure assumption if coldMains certainty is 'assumed' or 'unknown'
  if (coldMainsData.certainty === 'unknown') {
    assumptions.push(`Water pressure test values assumed as ${mainsPressure} bar / ${flowRate} L/min`);
  } else if (coldMainsData.certainty === 'partial') {
    assumptions.push(`Water pressure test partially complete - some values assumed`);
  }
  // Only mention gas availability if it wasn't provided AND couldn't be inferred from boiler type
  if (input.gasAvailable === undefined && !isGasBoiler(currentBoiler)) {
    assumptions.push(`Gas availability assumed`);
  }
  const hasLowTempEmitterEvidence = !!(input.emitters?.lowTempSuitability && input.emitters.lowTempSuitability !== 'unknown');
  if ((!input.radiatorSuitability || input.radiatorSuitability === "unknown") && !hasLowTempEmitterEvidence) {
    assumptions.push(`Radiator suitability not confirmed (assessment needed before heat pump installation)`);
  }
  
  // Add confidence assumptions
  assumptions.push(...confidenceResult.assumptions);
  
  // 7. Prepare output - use topOptions (filtered by numRecommendations) instead of all options
  const [primary, ...alternatives] = topOptions;
  const includeExcludedInConsidered = input.includeExcludedInConsidered ?? true;
  const consideredOptions = includeExcludedInConsidered
    ? optionsRanked
    : optionsRanked.filter(option => !option.excluded);
  
  // 7b. Resolve baseline smart control assumption
  // This must happen AFTER all options are created but BEFORE baseline calculation
  const allOptions = [primary, ...alternatives, ...consideredOptions];
  const baselineSmartControl = resolveBaselineSmartControl(input, allOptions, evidence);
  
  // Apply confidence and recommendation copy to primary and alternatives
  if (primary) {
    primary.confidence = confidenceResult.band;
    primary.openingSentence = generateOpeningSentence(confidenceResult.band);
    primary.confidenceExplanation = generateConfidenceExplanation(confidenceResult.band);
    primary.whyThisWon = generateWhyThisWon(
      primary.boiler,
      primary.water,
      primary.pros,
      primary.futureUpgradePositioning || ""
    );
    primary.closingLine = generateClosingLine(true);
    
    // Add heat pump framing if primary is not a heat pump
    const isPrimaryHeatPump = primary.boiler === "air_source_hp" || primary.boiler === "ground_source_hp";
    primary.heatPumpFraming = generateHeatPumpFraming(isPrimaryHeatPump, true);
  }
  
  alternatives.forEach((alt, index) => {
    alt.confidence = confidenceResult.band;
    alt.openingSentence = generateOpeningSentence(confidenceResult.band);
    alt.confidenceExplanation = generateConfidenceExplanation(confidenceResult.band);
    alt.whyThisWon = generateWhyThisWon(
      alt.boiler,
      alt.water,
      alt.pros,
      alt.futureUpgradePositioning || ""
    );
    alt.closingLine = generateClosingLine(false);
    
    // Add heat pump framing for non-heat-pump alternatives
    const isAltHeatPump = alt.boiler === "air_source_hp" || alt.boiler === "ground_source_hp";
    alt.heatPumpFraming = generateHeatPumpFraming(isAltHeatPump, false);
  });

  // 8. Contamination analysis (optional feature)
  let contaminationAnalysis: ContaminationAnalysis | undefined;
  if (input.contaminationSymptoms || input.contaminationSystemFactors) {
    const contaminationInput = {
      symptoms: input.contaminationSymptoms || {},
      systemFactors: input.contaminationSystemFactors || {},
    };

    // Estimate annual heat cost for LCOO context
    // If LCOO is available, use its running cost; otherwise estimate from heat loss
    let annualHeatCostGBP = 1200; // Default fallback
    if (lcooCfg && primary.lcoo) {
      annualHeatCostGBP = primary.lcoo.annualRunningGBP || 1200;
    } else if (heatLossResult.heatLossKw) {
      // Rough estimate: 2000 hours/year at 95% utilization, gas at ~6p/kWh
      annualHeatCostGBP = heatLossResult.heatLossKw * 2000 * 0.95 * 0.06;
    }

    const lcooContext = {
      annual_heat_cost_gbp: annualHeatCostGBP,
      avg_breakdown_cost_gbp: 400, // Default, could be configurable
    };

    try {
      contaminationAnalysis = analyzeContamination(contaminationInput, lcooContext);
    } catch (error) {
      console.error('Contamination analysis failed:', error);
      // Continue without contamination analysis if it fails
    }
  }

  // 9. Baseline "Cost of Doing Nothing" calculation (optional feature)
  let baselineResult: BaselineResult | undefined;
  if (input.baselineEnabled) {
    // Estimate annual heat demand if not provided
    const annualHeatDemand = lcooCfg?.annualUsefulHeatKwh || estimateAnnualHeatDemand(heatLossResult.heatLossKw || 10);

    // Determine sludge risk with priority: input → contamination → NONE
    let sludgeRisk: SludgeRisk = 'NONE';
    if (input.sludgeRisk) {
      // Priority 1: User-provided sludge risk override
      sludgeRisk = input.sludgeRisk;
    } else if (contaminationAnalysis) {
      // Priority 2: Derive from contamination analysis
      sludgeRisk = contaminationSeverityToSludgeRisk(contaminationAnalysis.score.severity);
    }
    // Priority 3: Default to 'NONE' (already set)

    // Get fuel price from LCOO config or use default
    const fuelType = input.currentFuelType || 'gas';
    const fuelPrice = fuelType === 'gas'
      ? (lcooCfg?.tariffs?.gasPPerKwh || 5.93)
      : (lcooCfg?.tariffs?.elecPPerKwh || 27.69);

    // Resolve SEDBUK efficiency if GC number or make/model provided
    let sedbukResolution;
    if (input.currentSystemEfficiency?.gcNumber || 
        (input.currentSystemEfficiency?.boilerMake && input.currentSystemEfficiency?.boilerModel)) {
      sedbukResolution = resolveSedbuk({
        gcNumber: input.currentSystemEfficiency.gcNumber,
        make: input.currentSystemEfficiency.boilerMake,
        model: input.currentSystemEfficiency.boilerModel,
        // Use property age and heat loss as fallback context
        ageBand: input.propertyAgeBand,
      });
      
      // Track SEDBUK resolution in evidence
      if (sedbukResolution.matchedBoiler) {
        evidence.appliedInputs.sedbukResolution = {
          source: sedbukResolution.source,
          confidence: sedbukResolution.confidence,
          band: sedbukResolution.band,
          efficiency: sedbukResolution.value,
          matchedBoiler: sedbukResolution.matchedBoiler
        };
        if (input.currentSystemEfficiency.gcNumber) {
          evidence.appliedInputs.gcNumber = input.currentSystemEfficiency.gcNumber;
        }
        if (input.currentSystemEfficiency.boilerMake && input.currentSystemEfficiency.boilerModel) {
          evidence.appliedInputs.boilerMake = input.currentSystemEfficiency.boilerMake;
          evidence.appliedInputs.boilerModel = input.currentSystemEfficiency.boilerModel;
        }
      } else {
        // GC lookup failed
        const reason = input.currentSystemEfficiency.gcNumber
          ? `GC number ${input.currentSystemEfficiency.gcNumber} not found in SEDBUK database`
          : `Make/model ${input.currentSystemEfficiency.boilerMake} ${input.currentSystemEfficiency.boilerModel} not found in SEDBUK database`;
        evidence.fallbacksUsed.push({
          field: 'baselineEfficiency',
          reason,
          value: sedbukResolution.value
        });
      }
    }

    // ========== CANONICAL SEASONAL EFFICIENCY RESOLUTION ==========
    // Use the single source of truth for efficiency throughout the system.
    // In UK domestic context, SEDBUK IS seasonal efficiency - no distinction needed.
    const seasonalEfficiency: SeasonalEfficiency = resolveSeasonalEfficiency({
      // GC or make/model lookup result (highest priority)
      gcLookupResult: sedbukResolution ? {
        value: sedbukResolution.value,
        band: sedbukResolution.matchedBoiler?.band as SedbukBand | undefined,
        source: sedbukResolution.source === 'model_lookup' ? 'model_lookup' : 'gc_lookup',
      } : undefined,
      // User-provided values (fallback priorities)
      userSeasonalEfficiency: input.currentSystemEfficiency?.sedbukSeasonal,
      userSedbukBand: input.currentSystemEfficiency?.sedbukBand as SedbukBand | undefined,
      epcBand: input.currentSystemEfficiency?.epcBand as SedbukBand | undefined,
    });

    // Track the resolved efficiency in evidence
    evidence.appliedInputs.seasonalEfficiency = {
      value: seasonalEfficiency.value,
      band: seasonalEfficiency.band,
      source: seasonalEfficiency.source,
      confidence: seasonalEfficiency.confidence,
      sourceDescription: seasonalEfficiency.sourceDescription,
    };

    // Only report missing inputs if we fell back to ASSUMED
    if (seasonalEfficiency.source === 'ASSUMED' && input.currentSystemEfficiency?.source === 'SEDBUK') {
      evidence.missingInputs.push('currentSystemEfficiency: no GC number, make/model, or band provided');
    }
    
    // Normalize boiler age from install year or direct age
    const boilerAge = normalizeBoilerAge(input.boilerInstallYear, input.boilerAgeYears);

    const baselineInput: BaselineInput = {
      annualHeatDemand_kWh: annualHeatDemand,
      // Use the CANONICAL seasonal efficiency object - single source of truth
      seasonalEfficiency,
      fuelType: fuelType,
      fuelUnitPrice_pPerKwh: fuelPrice,
      sludgeRisk: sludgeRisk,
      systemAge_years: input.existingSystemAge,
      analysisYears: lcooCfg?.periodYears || 20,
      // Include standing charge if requested
      standingCharge_gbpPerYear: input.includeStandingCharge ? input.standingCharge_gbpPerYear : undefined,
      // Include boiler age and make for design-life assessment
      boilerMake: input.currentSystemEfficiency?.boilerMake,
      boilerAge_years: boilerAge,
    };

    try {
      baselineResult = calculateBaselineCost(baselineInput);
    } catch (error) {
      console.error('Baseline calculation failed:', error);
      // Continue without baseline if it fails
    }
  }

  // 9b. Apply pricing overrides (optional feature)
  let pricingSummary: import('./types').SystemRecOutput['pricingSummary'];
  const pricingOverrides = input.pricingOverrides;
  
  if (pricingOverrides?.entries?.length) {
    const years = pricingOverrides.years ?? 10;
    const currency = pricingOverrides.currency ?? 'GBP';
    const mixProportional = pricingOverrides.mixProportionalCapex ?? false;
    const overridesProvided = true;
    const proportionalCapexDisabled = overridesProvided && !mixProportional;
    
    // Track evidence for pricing overrides
    evidence.appliedInputs.pricingOverridesProvided = overridesProvided;
    evidence.appliedInputs.overridesCount = pricingOverrides.entries.length;
    evidence.appliedInputs.pricingYears = years;
    evidence.appliedInputs.pricingCurrency = currency;
    evidence.appliedInputs.mixProportionalCapex = mixProportional;
    evidence.appliedInputs.proportionalCapexDisabled = proportionalCapexDisabled;
    
    const usedIds = new Set<string>();
    
    // Apply pricing to primary
    const applyPricingToOption = (option: RecommendationOption) => {
      const matchResult = matchCapex(option, pricingOverrides);
      
      option.pricing = {
        years,
        currency,
        capex: matchResult.capex,
        capexMatch: matchResult.match,
        capexSource: 'NONE',
        matchedOverrideId: matchResult.matchedOverrideId,
      };
      
      if (matchResult.match !== 'NONE' && matchResult.capex !== undefined) {
        // Override matched
        option.pricing.capexSource = 'OVERRIDE';
        option.pricing.currency = matchResult.currency!;
        if (matchResult.matchedOverrideId) {
          usedIds.add(matchResult.matchedOverrideId);
        }
        
        // Track fallback usage
        if (matchResult.match === 'FALLBACK_NO_VARIANT') {
          evidence.fallbacksUsed.push({
            field: 'pricingOverride',
            reason: `Matched ${option.title} using fallback (ignoring variant)`,
            value: matchResult.capex
          });
        }
      } else if (!proportionalCapexDisabled) {
        // Use proportional capex if allowed
        option.pricing.capexSource = 'PROPORTIONAL';
        // Note: Proportional capex calculation not implemented in this version.
        // Future implementation would estimate capex based on system complexity/size.
        // For now, capex remains undefined when proportional is enabled but no override matches.
      } else {
        // No override and proportional disabled
        option.pricing.capexSource = 'NONE';
      }
      
      // Calculate total cost if we have both capex and running cost
      if (option.pricing.capex !== undefined && option.lcoo?.annualRunningGBP !== undefined) {
        option.pricing.totalCost = option.pricing.capex + (option.lcoo.annualRunningGBP * years);
      }
    };
    
    applyPricingToOption(primary);
    alternatives.forEach(applyPricingToOption);
    
    // Build pricing summary
    const unusedOverrideIds = collectUnusedOverrides(pricingOverrides, usedIds);
    pricingSummary = {
      years,
      currency,
      overridesProvided,
      overridesUsedCount: usedIds.size,
      unusedOverrideIds,
      proportionalCapexDisabled,
      mixProportionalCapex: mixProportional
    };
  }

  // 10. Add deltaVsBaseline to each option (if baseline exists)
  if (baselineResult) {
    const analysisYears = lcooCfg?.periodYears || 20;

    // Helper to add delta to an option
    const addDeltaToOption = (option: RecommendationOption) => {
      if (option.lcoo && typeof option.lcoo.totalGBP === 'number') {
        const optionTotalCost = option.lcoo.totalGBP;
        const optionUpfrontCost = option.lcoo.upfrontGBP || 0;
        const baselineUpfrontCost = 0; // Baseline is "do nothing" so no upfront cost

        const delta = compareToBaseline(
          baselineResult!,
          optionTotalCost,
          optionUpfrontCost,
          baselineUpfrontCost,
          analysisYears
        );

        option.deltaVsBaseline = delta;
      }
    };

    // Add delta to primary
    addDeltaToOption(primary);

    // Add delta to each alternative
    for (const alt of alternatives) {
      addDeltaToOption(alt);
    }
  }

  // 11. Generate MoneyImpact data (if baseline and LCOO exist)
  const moneyImpact = (baselineResult && lcooCfg)
    ? generateMoneyImpact({
        rulesetVersion: RULESET_VERSION,
        generatedAt: new Date().toISOString(),
        primary,
        alternatives,
        consideredOptions: [],
        assumptions,
        inputEcho: { ...input },
        contaminationAnalysis,
        baselineResult,
      }, lcooCfg.periodYears || 20)
    : undefined;

  // 12. Generate Year-1 comparison (if baseline exists)
  const year1 = baselineResult
    ? generateYear1Comparison({
        state: isDeterminate ? "determinate" : "exploratory",
        rulesetVersion: RULESET_VERSION,
        generatedAt: new Date().toISOString(),
        primary,
        alternatives,
        consideredOptions: [],
        assumptions,
        inputEcho: { ...input },
        contaminationAnalysis,
        baselineResult,
        baselineSmartControl,
        moneyImpact: moneyImpact ?? undefined,
      })
    : undefined;

  // 13. Generate decision summary (if pricing data available)
  const decisionSummary = buildDecisionSummary({
    state: isDeterminate ? "determinate" : "exploratory",
    rulesetVersion: RULESET_VERSION,
    generatedAt: new Date().toISOString(),
    primary,
    alternatives,
    assumptions,
    inputEcho: { ...input },
    contaminationAnalysis,
    baselineResult,
    moneyImpact,
    year1,
    evidence,
    pricingSummary,
  } as import('./types').SystemRecOutput);

  // 14. Generate home summary facts for presentation layer
  const homeSummaryFacts = generateHomeSummaryFacts(
    retrofitLevel,
    lowTempReadiness,
    emitterProfile,
    retrofitProfile
  );

  // 15. Compute confidence summary
  const confidenceSummary = computeConfidenceSummary(input);

  // 16. Generate red flags
  const redFlags = generateRedFlags(input);

  // 17. Generate what-could-change bullets
  const whatCouldChangeRecommendation = generateWhatCouldChange(input);

  // 18. Build assumption manifest (energy prices + carbon factors are the wired assumptions)
  const assumptionManifest = buildManifestFor(['energy_prices', 'carbon_factors']);

  // Build the output (before experience fit)
  const output: SystemRecOutput = {
    state: isDeterminate ? "determinate" : "exploratory",
    rulesetVersion: RULESET_VERSION,
    generatedAt: new Date().toISOString(),
    primary,
    alternatives,
    consideredOptions,
    assumptions,
    inputEcho: { ...input }, // Safe copy for audit
    contaminationAnalysis,
    baselineResult,
    baselineSmartControl,
    moneyImpact: moneyImpact ?? undefined,
    year1: year1 ?? undefined,
    assumptionManifest,  // Traceable assumptions for energy prices + carbon factors
    evidence,  // Add evidence trace
    pricingSummary,
    decisionSummary,
    derivedMetrics: {
      retrofitLevel,
      lowTempReadiness,
      emitterProfile,
      retrofitProfile,
    },
    homeSummaryFacts,
    confidenceSummary,
    redFlags: redFlags.length > 0 ? redFlags : undefined,
    whatCouldChangeRecommendation,
    heatLossSummary: heatLossSummary ?? undefined,
    simpleHeatLossResult: simpleHeatLossResult ?? undefined,
  };

  // Apply experience fit layer (post-engine, presentation only)
  return evaluateExperienceFit(input, output);
}
